import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Check, ChevronLeft, ChevronRight, ChevronDown, Menu, Play,
  Compass, Zap, Crosshair, Map as MapIcon,
  Lightbulb, AlertTriangle, Quote as QuoteIcon,
} from "lucide-react";
import { GOLD } from "../constants/theme";
import MasterclassComments from "./MasterclassComments";
import { supabase } from "../../supabaseClient";
import { ArchetypeAnimation } from "./ArchetypeAnimation";
import { PgDiagnostic } from "./PgDiagnostic";
import { ArchetypeQuiz } from "./ArchetypeQuiz";
import { MasterclassEmailGate, hasCapturedLead } from "./MasterclassEmailGate";
import { BalanceGraphic, SciencePanel } from "./BalanceGraphics";
import { renderInline } from "./inlineMarkdown";
import { ConceptGraphic } from "../concepts/AttractorGraphics";
import { chapterDisplayTitle } from "./chapterTitle";
import { haptic } from "../../utils/nativeBridge";
import { useMasterclassProgress, logMasterclassPage } from "./useMasterclassProgress";
import { useReadTracking } from "./useReadTracking";
import { asStep } from "./actionStep";
import { articleBlock, Reveal, ARTICLE_CSS, DISPLAY as ARTICLE_DISPLAY, useArticleFont, accent as articleAccent, SectionContents,
         ARTICLE_BODY, ARTICLE_LH, bodyColor } from "./ArticleBlocks";
import { PassingGraphic } from "./PassingGraphics";
import { AccuracyGraphic } from "./PassingAccuracyGraphics";
import { PassingMatrix, QUADRANTS as PASSING_MATRIX_QUADRANTS } from "./PassingMatrix";
import { VideoLessons } from "./VideoLessons";
import { SkillTabs } from "./SkillTabs";
import { JourneyLayout } from "./JourneyLayout";
import { PillSheetModal } from "./PillSheet";
import { ModuleIntro, introSeen, markIntroSeen } from "./ModuleIntro";
// The science and philosophy layer. Same renderer, so a concept module can
// also use any masterclass block and a masterclass can borrow a framework.
import {
  useModuleResponses, Prose, TimedRounds, MasterclassLink, PodcastLink,
  BigIdea, Scenario, BamInsight, Framework, Misconception, Reflection, RelatedConcepts,
  CertDepth, ScienceNerd, ResearchCard, MultipleChoice, WrittenResponse, Diagnostic, ApplyToAthlete, ConceptTable,
} from "./conceptBlocks";
import { CertTeaser } from "../concepts/CertTeaser";
import { authHeaders } from "../../utils/authHeaders";

// Lucide icon map for deck cards. Cards can specify `icon: "compass"`
// in their config and we look up the component here. New icons get
// added to this map as they're needed; keeps a typed surface.
//
// IMPORTANT: lucide's `Map` icon is imported as `MapIcon` to avoid
// shadowing the native global `Map` constructor — the DrillCollection
// cache below uses `new Map()` and previously crashed at module load
// because the lucide forwardRef object isn't a constructor.
const DECK_ICONS = {
  compass:   Compass,    // Floor Architect (navigator / probing)
  zap:       Zap,        // Downhill Creator (burst)
  crosshair: Crosshair,  // Shot-First (target)
  map:       MapIcon,    // Quarterback (reading the field)
};

const storageKey = (moduleKey, suffix) => `bam_mc_${moduleKey}_${suffix}`;

// Read a UTM-style source from the current URL. Lead-magnet links land
// the user at #/free/modern-point-guard, so the natural place for a
// campaign param is AFTER the hash (e.g.
// #/free/modern-point-guard?src=instagram). HashRouter doesn't surface
// that as window.location.search, so we parse it out of the hash
// ourselves. Falls back to window.location.search for the alt pattern
// (?src=instagram before the hash), then defaults to "free-preview".
function readUtmSource() {
  if (typeof window === "undefined") return "free-preview";
  // First check hash query string (most common pattern for our routes)
  const hash = window.location.hash || "";
  const hashQ = hash.indexOf("?");
  if (hashQ >= 0) {
    const src = new URLSearchParams(hash.slice(hashQ + 1)).get("src");
    if (src) return src.slice(0, 80);
  }
  // Fallback to the pre-hash query string
  const fromSearch = new URLSearchParams(window.location.search).get("src");
  if (fromSearch) return fromSearch.slice(0, 80);
  return "free-preview";
}

// Mobile-cutover hook. Matches the 768px threshold used by PublicPreview
// and LandingPage so the lead-magnet preview switches to single-column
// layout + sticky bottom CTA in lockstep with the rest of the marketing
// surface.
function useIsMobile(breakpoint = 768) {
  const [m, setM] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < breakpoint
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setM(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return m;
}

function useLocal(key, initial) {
  const [v, setV] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw == null ? initial : JSON.parse(raw);
    } catch { return initial; }
  });
  // A ref of the current value so the setter can resolve a function
  // updater without reading state inside a reducer.
  const cur = useRef(v);
  cur.current = v;
  // Two things this fixes, both latent until something needed them:
  //
  // 1. A function updater used to be written to localStorage verbatim, so
  //    `set(prev => ...)` stored the string of a function and the next
  //    load threw it away. Only direct values were ever passed, so nobody
  //    hit it; the account sync needs updaters to merge safely.
  // 2. The setter was a new function on every render. Anything holding it
  //    in a dependency array re-ran on every render, which for the sync
  //    hook below would mean refetching progress forever.
  const set = useCallback((next) => {
    const value = typeof next === "function" ? next(cur.current) : next;
    cur.current = value;
    setV(value);
    try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
  }, [key]);
  return [v, set];
}

/**
 * Body copy for every masterclass.
 *
 * The type scale is the article one (17.5 / 1.8, and `bodyColor` rather than
 * textMid, which is tuned for 13-15px labels on cards and goes washed over a
 * long read). It started as a per-module opt-in for the passing masterclass
 * and Coleman asked for it everywhere, so it lives here rather than in
 * ArticleBlocks: the type is the house voice, the article LAYOUT is still an
 * opt-in on top of it.
 */
function Paragraph({ md, C, dark }) {
  const lines = md.split("\n");
  const out = [];
  const body = { fontSize: ARTICLE_BODY, lineHeight: ARTICLE_LH, color: bodyColor(dark) };
  let buf = [];
  const flushBuf = () => {
    if (buf.length === 0) return;
    out.push(<p key={out.length} style={{...body,margin:"0 0 16px"}}>{renderInline(buf.join(" "))}</p>);
    buf = [];
  };
  // A numbered list, for a process where the order is the point. Without
  // this, "1. x" and "2. y" on separate lines join into one paragraph
  // reading "1. x 2. y", because unprefixed lines are soft-wrapped prose.
  let numBuf = [];
  const flushNums = () => {
    if (numBuf.length === 0) return;
    out.push(
      <ol key={out.length} style={{margin:"0 0 18px",paddingLeft:26}}>
        {numBuf.map((t, i) => (
          <li key={i} style={{...body,lineHeight:1.7,marginBottom:8,paddingLeft:4}}>{renderInline(t)}</li>
        ))}
      </ol>
    );
    numBuf = [];
  };
  let bulletBuf = [];
  const flushBullets = () => {
    if (bulletBuf.length === 0) return;
    out.push(
      <ul key={out.length} style={{margin:"0 0 18px",paddingLeft:24}}>
        {bulletBuf.map((t, i) => (
          <li key={i} style={{...body,lineHeight:1.7,marginBottom:8}}>{renderInline(t)}</li>
        ))}
      </ul>
    );
    bulletBuf = [];
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const num = line.trimStart().match(/^(\d+)\.\s+(.*)$/);
    if (line.trimStart().startsWith("- ")) {
      flushBuf(); flushNums();
      bulletBuf.push(line.trimStart().slice(2));
    } else if (num) {
      flushBuf(); flushBullets();
      numBuf.push(num[2]);
    } else if (line.trim() === "") {
      flushBullets(); flushNums(); flushBuf();
    } else {
      flushBullets(); flushNums();
      buf.push(line);
    }
  }
  flushBullets();
  flushNums();
  flushBuf();
  return <>{out}</>;
}


function Heading({ level, text, C, id }) {
  // Level 3 headings act as section breaks inside a chapter. Wrap them
  // with an editorial divider — gold gradient rule on each side of a
  // centered gold dot — so the eye registers a real section boundary,
  // not just bigger text. Other levels render as plain headings.
  if (level === 3) {
    return (
      <div style={{ margin: "40px 0 14px", scrollMarginTop: 80 }}>
        <div aria-hidden="true" style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginBottom: 18,
        }}>
          <div style={{
            flex: 1,
            height: 1,
            background: `linear-gradient(90deg, transparent, ${GOLD}33, transparent)`,
          }}/>
          <div style={{
            width: 5,
            height: 5,
            borderRadius: "50%",
            background: GOLD,
            boxShadow: `0 0 10px ${GOLD}77`,
            flexShrink: 0,
          }}/>
          <div style={{
            flex: 1,
            height: 1,
            background: `linear-gradient(90deg, transparent, ${GOLD}33, transparent)`,
          }}/>
        </div>
        <h3 id={id} style={{
          fontFamily: ARTICLE_DISPLAY, fontSize: 24, fontWeight: 700, color: C.text,
          margin: 0, letterSpacing: "-0.012em", lineHeight: 1.25,
          scrollMarginTop: 80,
        }}>{text}</h3>
      </div>
    );
  }
  const size = level === 2 ? 24 : 19;
  return (
    <h3 id={id} style={{fontFamily:ARTICLE_DISPLAY,fontSize:size,fontWeight:700,color:C.text,margin:"30px 0 12px",letterSpacing:"-0.012em",lineHeight:1.25,scrollMarginTop:80}}>{text}</h3>
  );
}

// PullQuote — editorial-style pull quote with three deliberate moves:
//   1. Translucent gold quote glyph anchored top-left, behind the text.
//      Visual cue that this is a quote without saying "QUOTE".
//   2. Asymmetric typography: the first sentence renders as a larger
//      serif italic "lead" (the punch line), the rest drops to body
//      weight. Eye lands on the load-bearing phrase first.
//   3. Bottom-right gold rule + optional attribution. Magazine-style
//      anchor for the right edge so the block doesn't float.
function PullQuote({ md, attribution, prominent, C }) {
  // Split on the first period for the asymmetric treatment. If the quote
  // is a single sentence, the whole thing becomes the lead; the rest
  // section stays empty. We trim and discard the period at the boundary
  // so it lives at the end of the lead, not orphaned at the start of
  // the rest.
  const periodIdx = md.indexOf(".");
  const hasRest = periodIdx > 0 && periodIdx < md.length - 1;
  const lead = hasRest ? md.slice(0, periodIdx + 1) : md;
  const rest = hasRest ? md.slice(periodIdx + 1).trim() : "";

  // `prominent` is set on the first/thesis pullquote of a masterclass
  // to give it editorial-cover weight: bigger glyph, bigger lead type.
  // Mobile scaling is handled by the .mc-pullquote-* media-query rules
  // in the reader's <style> block; inline values are the desktop spec.
  const leadSize = prominent ? 32 : 26;
  const glyphSize = prominent ? 140 : 110;
  const glyphOpacity = prominent ? 0.22 : 0.18;

  return (
    <blockquote className="mc-pullquote-wrap" style={{
      position: "relative",
      margin: prominent ? "44px 0 36px" : "36px 0 30px",
      padding: "12px 8px 4px 60px",
      background: "transparent",
    }}>
      {/* Editorial quote glyph — large translucent gold mark anchored
          to the top-left. Sits behind the text and acts as the visual
          anchor for the block. */}
      <div aria-hidden="true" className="mc-pullquote-glyph" style={{
        position: "absolute",
        top: prominent ? -22 : -14,
        left: 0,
        fontFamily: "Georgia, 'Times New Roman', serif",
        fontSize: glyphSize,
        lineHeight: 0.8,
        color: GOLD,
        opacity: glyphOpacity,
        userSelect: "none",
        pointerEvents: "none",
        fontWeight: 700,
      }}>“</div>
      {/* Lead — serif italic, larger. The punch line. */}
      <div className="mc-pullquote-lead" style={{
        fontFamily: "Georgia, 'Times New Roman', serif",
        fontStyle: "italic",
        fontSize: leadSize,
        lineHeight: 1.35,
        color: C.text,
        fontWeight: prominent ? 600 : 500,
        letterSpacing: -0.3,
      }}>{renderInline(lead)}</div>
      {/* Rest of the quote, if any. Body weight, smaller. */}
      {rest && (
        <div className="mc-pullquote-rest" style={{
          marginTop: 12,
          fontSize: 16,
          lineHeight: 1.6,
          color: C.textMid,
          fontWeight: 400,
        }}>{renderInline(rest)}</div>
      )}
      {/* Bottom-right accent rule — short gradient line that anchors
          the right edge and gives the attribution something to hang on. */}
      <div aria-hidden="true" style={{
        marginTop: 18,
        marginLeft: "auto",
        height: 1,
        width: 64,
        background: `linear-gradient(90deg, transparent, ${GOLD})`,
      }}/>
      {attribution && (
        <div style={{
          marginTop: 8, textAlign: "right",
          fontSize: 11, fontWeight: 700, color: GOLD,
          letterSpacing: 1.4, textTransform: "uppercase",
        }}>{attribution}</div>
      )}
    </blockquote>
  );
}

// ── Reader helpers: word count + read time + chapter TOC ────────────

// Pull every visible string out of a block tree. Used for word count
// estimates. Skips video URLs and embed IDs since coaches don't read
// those.
function blockText(block) {
  if (!block) return "";
  if (block.kind === "text" || block.kind === "pullquote") return block.md || "";
  if (block.kind === "heading") return block.text || "";
  if (block.kind === "callout") return `${block.title || ""} ${block.body || ""}`;
  if (block.kind === "principles") return (block.items || []).map(i => `${i.title || ""} ${i.body || ""}`).join(" ");
  if (block.kind === "compare") return `${block.leftTitle || ""} ${block.rightTitle || ""} ${(block.leftItems || []).join(" ")} ${(block.rightItems || []).join(" ")}`;
  if (block.kind === "checklist") return `${block.title || ""} ${(block.items || []).join(" ")}`;
  if (block.kind === "assess") return block.prompt || "";
  if (block.kind === "multipick") return `${block.prompt || ""} ${(block.options || []).join(" ")}`;
  if (block.kind === "actionSteps") return (block.items || []).join(" ");
  if (block.kind === "cta") return `${block.label || ""} ${block.note || ""}`;
  if (block.kind === "science") return `${block.title || ""} ${block.body || ""} ${(block.items || []).map(i => `${i.term || ""} ${i.def || ""}`).join(" ")}`;
  // The graphic's caption is the only text in it, and the matrix's text lives
  // in its own module, so search has something to match on for both.
  if (block.kind === "accuracyGraphic") return block.caption || "";
  if (block.kind === "passingMatrix") {
    return PASSING_MATRIX_QUADRANTS
      .map(q => `${q.title} ${q.means} ${(q.train || []).join(" ")}`).join(" ");
  }
  if (block.kind === "videoLessons") return (block.segments || []).map(s => `${s.title || ""} ${(s.blocks || []).map(blockText).join(" ")}`).join(" ");
  if (block.kind === "deck") return (block.cards || []).map(c => `${c.title || ""} ${(c.blocks || []).map(blockText).join(" ")}`).join(" ");
  if (block.kind === "worksheet") return `${block.title || ""} ${(block.sections || []).map(s => `${s.label || ""} ${(s.fields || []).map(f => f.label || "").join(" ")}`).join(" ")}`;
  return "";
}

function countWords(s) {
  return (s || "").trim().split(/\s+/).filter(Boolean).length;
}

// 220 wpm is the standard reading rate for content like this. Round
// up to nearest minute, floor at 1.
function readMinutes(chapter) {
  const words = countWords((chapter.blocks || []).map(blockText).join(" "));
  return Math.max(1, Math.ceil(words / 220));
}

// Video chapters should show the actual runtime, not a word-count "read time".
// We pull the duration off the Mux asset (cached) and label it "watch".
const muxDurCache = {};
function fmtRuntime(seconds) {
  const s = Math.round(seconds || 0);
  if (s <= 0) return null;
  const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${Math.max(1, m)} min`;
}
function chapterVideoId(chapter) {
  const v = (chapter.blocks || []).find(b => b.kind === "video" && b.muxPlaybackId);
  return v ? v.muxPlaybackId : null;
}
async function fetchMuxDuration(playbackId) {
  if (muxDurCache[playbackId] !== undefined) return muxDurCache[playbackId];
  const base = (typeof window !== "undefined" && window.Capacitor) ? "https://platform.byanymeanscoaches.com" : "";
  try {
    const r = await fetch(`${base}/api/mux-asset-status?playbackId=${encodeURIComponent(playbackId)}`, { headers: await authHeaders() });
    const j = await r.json();
    const lbl = fmtRuntime(j && j.duration);
    muxDurCache[playbackId] = lbl;
    return lbl;
  } catch { muxDurCache[playbackId] = null; return null; }
}

// Walk a chapter's blocks and return any heading objects with id +
// text so we can render an in-chapter TOC. Only includes H3+ headings
// at the top level (not inside decks) since deck contents have their
// own navigation.
function chapterTOC(chapter) {
  const out = [];
  (chapter.blocks || []).forEach((b, i) => {
    if (b.kind === "heading" && (b.level === 3 || b.level === 4)) {
      const id = `h-${chapter.id}-${i}`;
      out.push({ id, text: b.text, level: b.level });
    }
  });
  return out;
}

// useChapterProgress — track which H3 sections the user has scrolled
// past inside the current chapter. Returns the "active" heading id
// (the most-recent one above the top-of-viewport threshold) and a Set
// of all heading ids visited so far. Used to render progress dots in
// the inline TOC + the sticky mini-TOC.
function useChapterProgress(headings, scrollRef) {
  const [activeId, setActiveId] = useState(null);
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !headings || headings.length === 0) {
      setActiveId(null);
      return;
    }
    let raf = null;
    const compute = () => {
      raf = null;
      // The "active" heading is the LAST one whose top is above the
      // threshold line near the top of the scroll container. 140px is
      // far enough below the sticky top bar that the heading reads as
      // "current section" rather than "about to enter".
      const THRESHOLD = 140;
      let mostRecent = null;
      for (const h of headings) {
        const el = document.getElementById(h.id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top < THRESHOLD) mostRecent = h.id;
        else break;
      }
      setActiveId(mostRecent);
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(compute);
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    // Initial fire after layout settles.
    setTimeout(compute, 0);
    return () => {
      container.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [headings, scrollRef]);

  const visitedIds = useMemo(() => {
    if (!activeId) return new Set();
    const idx = headings.findIndex(h => h.id === activeId);
    return new Set(headings.slice(0, idx + 1).map(h => h.id));
  }, [activeId, headings]);

  return { activeId, visitedIds };
}

// Tiny anchor TOC at the top of long chapters (3+ H3s). Each item gets
// a progress dot: filled gold once the user has scrolled past that
// section, hollow before. The currently-active section's dot glows.
function ChapterTOC({ items, activeId, visitedIds, C, article }) {
  if (!items || items.length < 3) return null;
  // In an article this is the contents line under the headline, not a gold
  // panel: a rule, a quiet label, and the sections. The dots stay, because
  // "which of these have I read" is the one thing the list is actually for.
  return (
    <div style={article ? {
      margin: "0 0 34px", padding: "0 0 4px",
      borderTop: `1px solid ${C.border}`, paddingTop: 16,
    } : {
      margin: "0 0 24px",
      padding: "14px 16px",
      borderRadius: 10,
      border: `1px solid ${GOLD}33`,
      background: `linear-gradient(180deg, rgba(226,221,159,0.06), rgba(226,221,159,0.01))`,
    }}>
      <div style={article ? {
        fontSize: 10.5, fontWeight: 800, color: C.textDim,
        letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 12,
      } : {
        fontSize: 10, fontWeight: 800, color: GOLD,
        letterSpacing: 1.6, textTransform: "uppercase", marginBottom: 10,
      }}>In this chapter</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {items.map(it => {
          const isVisited = visitedIds && visitedIds.has(it.id);
          const isActive = activeId === it.id;
          return (
          <a key={it.id} href={`#${it.id}`}
            onClick={(e) => {
              // Smooth scroll, but don't change the URL hash (would
              // collide with the platform's hash router).
              e.preventDefault();
              const el = document.getElementById(it.id);
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              fontSize: 13,
              color: isActive ? C.text : isVisited ? C.text : C.textMid,
              fontWeight: isActive ? 700 : isVisited ? 600 : 500,
              textDecoration: "none",
              padding: "3px 0", cursor: "pointer",
            }}
            onMouseEnter={e => { e.currentTarget.style.color = GOLD; }}
            onMouseLeave={e => { e.currentTarget.style.color = isActive || isVisited ? C.text : C.textMid; }}
          >
            <span aria-hidden="true" style={{
              width: article ? 6 : 8, height: article ? 6 : 8, borderRadius: "50%",
              flexShrink: 0,
              background: isVisited ? GOLD : "transparent",
              border: isVisited ? "none" : `1.5px solid ${article ? C.border : `${GOLD}55`}`,
              boxShadow: isActive ? `0 0 10px ${GOLD}AA` : "none",
              transition: "background 0.2s, box-shadow 0.2s, border-color 0.2s",
            }}/>
            <span style={{flex: 1}}>{it.text}</span>
          </a>
          );
        })}
      </div>
    </div>
  );
}

// Sticky mini-TOC pill — appears at the top-right of the reading column
// once the user has scrolled past the inline ChapterTOC. Collapsed view
// shows current section name + position indicator (3/5). Click expands
// to the full heading list with progress dots, same as the inline TOC.
// Only shown for chapters with 2+ headings so it doesn't add noise to
// short chapters.
function StickyMiniToc({ items, activeId, visitedIds, C }) {
  const [open, setOpen] = React.useState(false);
  // Close on click anywhere outside the pill.
  React.useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      // Defer one tick so the open-trigger click doesn't also close it.
      setTimeout(() => {
        const target = e.target;
        if (!target.closest || !target.closest("[data-sticky-mini-toc]")) setOpen(false);
      }, 0);
    };
    window.addEventListener("click", onClick);
    return () => window.removeEventListener("click", onClick);
  }, [open]);
  if (!items || items.length < 2) return null;
  // Hide until user has actually scrolled into the chapter content
  // (activeId is set). Avoids floating UI at the top of the chapter.
  if (!activeId) return null;
  const activeItem = items.find(it => it.id === activeId);
  const activeIdx = items.findIndex(it => it.id === activeId);
  return (
    <div data-sticky-mini-toc className="mc-sticky-mini-toc" style={{
      position: "sticky",
      top: 12,
      // Right-align inside the reading column + pull the next content
      // up so the pill doesn't reserve a row of vertical space.
      marginLeft: "auto",
      marginBottom: -44,
      width: "fit-content",
      maxWidth: "min(100%, 380px)",
      zIndex: 30,
    }}>
      <div onClick={() => setOpen(o => !o)} style={{
        background: "rgba(20,20,20,0.92)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        border: `1px solid ${GOLD}55`,
        borderRadius: 100,
        boxShadow: `0 6px 22px rgba(0,0,0,0.55)`,
        padding: "6px 12px 6px 14px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        cursor: "pointer",
      }}>
        <span style={{
          fontSize: 10, fontWeight: 800, color: GOLD,
          letterSpacing: 1.4, textTransform: "uppercase",
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          {activeIdx + 1}/{items.length}
        </span>
        <span style={{ width: 1, height: 14, background: `${GOLD}33` }}/>
        <span className="mc-sticky-mini-toc-title" style={{
          fontSize: 12, fontWeight: 600, color: C.text,
          maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {activeItem ? activeItem.text : items[0].text}
        </span>
        <ChevronDown size={13} color={C.textMid} style={{
          transform: open ? "rotate(180deg)" : "none",
          transition: "transform 0.2s",
          flexShrink: 0,
        }}/>
      </div>
      {open && (
        <div style={{
          position: "absolute",
          top: "calc(100% + 6px)",
          right: 0,
          background: "rgba(15,15,15,0.96)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          border: `1px solid ${GOLD}55`,
          borderRadius: 12,
          boxShadow: `0 12px 32px rgba(0,0,0,0.65)`,
          padding: 8,
          width: 280,
          maxHeight: 360,
          overflow: "auto",
        }}>
          {items.map(it => {
            const isVisited = visitedIds && visitedIds.has(it.id);
            const isActive = activeId === it.id;
            return (
              <div key={it.id} onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                const el = document.getElementById(it.id);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "8px 10px", borderRadius: 6,
                cursor: "pointer",
                background: isActive ? `${GOLD}18` : "transparent",
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
              >
                <span style={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: isVisited ? GOLD : "transparent",
                  border: isVisited ? "none" : `1.5px solid ${GOLD}55`,
                  flexShrink: 0,
                  boxShadow: isActive ? `0 0 8px ${GOLD}99` : "none",
                }}/>
                <span style={{
                  fontSize: 13,
                  color: isActive ? C.text : isVisited ? C.text : C.textMid,
                  fontWeight: isActive ? 700 : 500,
                  flex: 1,
                }}>{it.text}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * `title` is a small line ABOVE the player; `caption` is the old line below.
 *
 * On the NBA article every clip is the answer to a sentence ("I wanted to
 * work on Rayan's ability to control rotational AND backwards momentum ->
 * start facing away from the rim"). Set as body prose those sentences read
 * as the argument and the clip reads as an interruption. Set small, directly
 * above the player, the pair reads as one thing: the problem, then the clip.
 */
function VideoBlock({ muxPlaybackId, caption, title, name, portrait, C }) {
  const Title = () => (title ? (
    <div style={{fontSize:14,lineHeight:1.5,color:C.textMid,fontWeight:600,margin:"0 0 8px"}}>
      {renderInline(title)}
    </div>
  ) : null);
  // Phone-shot player clips are portrait. In a 16:9 box `contain` letterboxes
  // them down to a thin strip with black on both sides; `portrait` gives them
  // a tall 9:16 frame capped to a sensible width and centred, so the footage
  // fills the frame instead. Landscape clips must NOT set this.
  const aspect = portrait ? "9 / 16" : "16 / 9";
  const fit = portrait ? "cover" : "contain";
  const wrap = portrait
    ? {margin:"18px auto",maxWidth:300}
    : {margin:"18px 0"};
  // Coming-soon placeholder when the Mux ID hasn't been set yet.
  //
  // `name` is the drill's own title, and it only matters here. Most captions
  // ARE the drill name, so the placeholder reads fine without it, but where
  // the caption is Coleman's prose about the clip the card opened with a
  // sentence and never said which clip was missing.
  if (!muxPlaybackId) {
    return (
      <div className="mc-video-wrap" style={wrap}>
        <Title/>
        <div style={{position:"relative",width:"100%",aspectRatio:aspect,background:"linear-gradient(135deg, #141414 0%, #0A0A0A 100%)",borderRadius:12,overflow:"hidden",border:`1px dashed ${C.cardBorder}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{textAlign:"center",padding:"24px"}}>
            <div style={{width:56,height:56,borderRadius:"50%",background:`${GOLD}14`,border:`1px solid ${GOLD}44`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 14px"}}>
              <Play size={22} color={GOLD} fill={GOLD}/>
            </div>
            <div style={{fontSize:12,fontWeight:800,color:GOLD,letterSpacing:1.2,textTransform:"uppercase",marginBottom:6}}>Coming soon</div>
            {name && (
              <div style={{fontSize:14,fontWeight:700,color:C.text,lineHeight:1.4,maxWidth:320,marginBottom:5}}>{name}</div>
            )}
            {(name ? caption : (caption || "Video uploading shortly.")) && (
              <div style={{fontSize:13,color:C.textMid,lineHeight:1.5,maxWidth:320}}>{name ? caption : (caption || "Video uploading shortly.")}</div>
            )}
          </div>
        </div>
      </div>
    );
  }
  // Use the Mux-hosted iframe player. It bypasses workspace domain restrictions
  // because the HLS request originates from player.mux.com (Mux's own domain).
  const src = `https://player.mux.com/${muxPlaybackId}?accent-color=${encodeURIComponent(GOLD)}`;
  // Mux renders a still from any playback id. Painting it behind the
  // iframe means a coach on gym wifi sees the drill immediately instead
  // of a black rectangle while the player boots, which is the difference
  // between waiting and leaving. Decorative, so it is hidden from
  // screen readers and never blocks.
  const poster = `https://image.mux.com/${muxPlaybackId}/thumbnail.jpg?width=640&fit_mode=preserve`;
  return (
    <div className="mc-video-wrap" style={wrap}>
      <Title/>
      {/* Three things against one bright line down the RIGHT edge of every
          clip. It was not the border, which is on all four sides and barely
          visible: sampling Coleman's screenshot found a single column of
          IMAGE-coloured pixels, varying down its length, on the right only.
          That is a child bleeding past a rounded parent's clip.

          `isolation: isolate` gives the container its own stacking context,
          which is what browsers need to clip exactly. The children carry the
          same radius so they round themselves rather than relying on it. And
          the poster drops from `cover` to `contain`, which cannot overflow
          at all: these clips are shot in portrait, so `cover` was scaling a
          tall image until it was wide enough for a 16:9 box and throwing
          away most of it. `contain` letterboxes it, which is what the player
          itself does a moment later, so the still now matches the video. */}
      <div style={{position:"relative",width:"100%",aspectRatio:aspect,background:"#000",borderRadius:12,overflow:"hidden",isolation:"isolate",border:`1px solid ${C.cardBorder}`}}>
        <img src={poster} alt="" aria-hidden="true" loading="lazy"
          style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:fit,opacity:0.55,borderRadius:12}}/>
        <iframe
          title={title || caption || "Video"}
          src={src}
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
          allowFullScreen
          style={{position:"absolute",inset:0,width:"100%",height:"100%",border:"none",borderRadius:12}}
        />
      </div>
      {caption && (
        <div style={{fontSize:12,color:C.textDim,marginTop:8,fontStyle:"italic",lineHeight:1.5}}>{caption}</div>
      )}
    </div>
  );
}

// Callout — tone-driven editorial card. Each tone gets a hanging tab
// at the top-left with an icon + short label (TIP / HEADS UP / QUOTE).
// The tab is the distinctive shape; the card body is intentionally
// minimal so the tab does the visual work.
const CALLOUT_TONES = {
  tip:   { Icon: Lightbulb,      label: "Tip",       border: `${GOLD}55`,              bg: `${GOLD}10`,                   accent: GOLD },
  warn:  { Icon: AlertTriangle,  label: "Heads up",  border: "rgba(248,113,113,0.35)", bg: "rgba(248,113,113,0.06)",      accent: "#F87171" },
  quote: { Icon: QuoteIcon,      label: "Quote",     border: "rgba(255,255,255,0.08)", bg: "rgba(255,255,255,0.02)",      accent: GOLD },
};
function Callout({ tone = "tip", title, body, C }) {
  const meta = CALLOUT_TONES[tone] || CALLOUT_TONES.tip;
  const Icon = meta.Icon;
  return (
    <div style={{ position: "relative", margin: "28px 0 18px" }}>
      {/* Hanging tab — sits above the top-left edge of the card. The
          tab carries the tone label + icon so the card body itself
          stays clean. Slight downward shadow gives the tab a "stuck on"
          feel like a sticker or paper tag. */}
      <div className="mc-callout-tab" style={{
        position: "absolute",
        top: -11,
        left: 16,
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "4px 9px 4px 8px",
        background: meta.accent,
        color: "#0A0A0A",
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: 1.4,
        fontFamily: "'Bebas Neue', Impact, sans-serif",
        borderRadius: "4px 4px 1px 1px",
        textTransform: "uppercase",
        zIndex: 1,
        boxShadow: `0 2px 6px rgba(0,0,0,0.45), 0 -1px 0 ${meta.accent}`,
      }}>
        <Icon size={11} strokeWidth={2.5}/>
        {meta.label}
      </div>
      {/* Card body */}
      <div style={{
        padding: "18px 18px 16px",
        borderRadius: 12,
        border: `1px solid ${meta.border}`,
        background: meta.bg,
        // Slight top-left corner cut helps the tab land cleanly against
        // the card edge instead of overlapping a round corner.
        borderTopLeftRadius: 6,
      }}>
        {title && (
          <div style={{
            fontSize: 14, fontWeight: 800, color: C.text,
            marginBottom: 8, letterSpacing: 0.1,
          }}>{title}</div>
        )}
        <div style={{
          fontSize: tone === "quote" ? 16 : 14,
          lineHeight: 1.65,
          color: tone === "quote" ? C.text : C.textMid,
          fontStyle: tone === "quote" ? "italic" : "normal",
          fontWeight: tone === "quote" ? 500 : 400,
        }}>
          {renderInline(body)}
        </div>
      </div>
    </div>
  );
}

function Principles({ items, C }) {
  return (
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",gap:12,margin:"18px 0"}}>
      {items.map(({ n, title, body, wide }) => (
        <div key={n} style={{padding:"16px 18px",background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:12,position:"relative",gridColumn:wide?"1 / -1":undefined}}>
          <div style={{fontSize:11,fontWeight:800,color:GOLD,letterSpacing:1,marginBottom:6}}>PRINCIPLE {n}</div>
          <div style={{fontSize:15,fontWeight:800,color:C.text,marginBottom:6}}>{title}</div>
          <div style={{fontSize:13,color:C.textMid,lineHeight:1.6}}>{body}</div>
        </div>
      ))}
    </div>
  );
}

function Compare({ leftTitle, leftItems, rightTitle, rightItems, C }) {
  const Col = ({ title, items, accent }) => (
    <div style={{flex:1,minWidth:0,padding:"14px 16px",background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:12}}>
      <div style={{fontSize:11,fontWeight:800,color:accent,textTransform:"uppercase",letterSpacing:.8,marginBottom:10}}>{title}</div>
      {items.map((t, i) => (
        <div key={i} style={{fontSize:13,color:C.textMid,lineHeight:1.55,padding:"4px 0"}}>• {renderInline(t)}</div>
      ))}
    </div>
  );
  return (
    <div style={{display:"flex",gap:12,margin:"18px 0",flexWrap:"wrap"}}>
      <Col title={leftTitle} items={leftItems} accent={GOLD}/>
      <Col title={rightTitle} items={rightItems} accent="#9CA3AF"/>
    </div>
  );
}

function Checklist({ id, title, items, moduleKey, chapterId, C }) {
  const key = storageKey(moduleKey, `check_${chapterId}_${id}`);
  const [checked, setChecked] = useLocal(key, {});
  const toggle = (i) => setChecked({ ...checked, [i]: !checked[i] });
  return (
    <div style={{margin:"18px 0",padding:"16px 18px",background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:12}}>
      <div style={{fontSize:12,fontWeight:800,color:C.text,textTransform:"uppercase",letterSpacing:.8,marginBottom:10}}>{title}</div>
      {items.map((t, i) => {
        const on = !!checked[i];
        return (
          <div key={i} onClick={() => toggle(i)} className="btn"
            style={{display:"flex",alignItems:"flex-start",gap:10,padding:"8px 0",cursor:"pointer"}}>
            <div style={{flexShrink:0,marginTop:2,width:18,height:18,borderRadius:5,border:`1.5px solid ${on?GOLD:C.border}`,background:on?GOLD:"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
              {on && <Check size={12} color="#111" strokeWidth={3}/>}
            </div>
            <div style={{fontSize:13.5,color:on?C.text:C.textMid,lineHeight:1.55,textDecoration:on?"line-through":"none",opacity:on?.75:1}}>{t}</div>
          </div>
        );
      })}
    </div>
  );
}

function Assess({ id, prompt, lowLabel, highLabel, moduleKey, chapterId, C }) {
  const key = storageKey(moduleKey, `assess_${chapterId}_${id}`);
  const [value, setValue] = useLocal(key, 0);
  return (
    <div style={{margin:"18px 0",padding:"16px 18px",background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:12}}>
      <div style={{fontSize:13.5,fontWeight:700,color:C.text,marginBottom:14,lineHeight:1.5}}>{prompt}</div>
      <div style={{display:"flex",gap:8,marginBottom:8}}>
        {[1,2,3,4,5].map(n => {
          const active = value === n;
          return (
            <button key={n} onClick={() => setValue(active ? 0 : n)} aria-label={`${n}`}
              style={{flex:1,padding:"10px 0",borderRadius:8,fontSize:14,fontWeight:800,cursor:"pointer",
                border:`1px solid ${active?GOLD:C.border}`,
                background:active?GOLD:"transparent",
                color:active?"#111":C.textMid,
                transition:"all .15s"}}>{n}</button>
          );
        })}
      </div>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:C.textDim}}>
        <span>{lowLabel||"Low"}</span>
        <span>{highLabel||"High"}</span>
      </div>
    </div>
  );
}

function MultiPick({ id, prompt, options, moduleKey, chapterId, C }) {
  const key = storageKey(moduleKey, `pick_${chapterId}_${id}`);
  const [selected, setSelected] = useLocal(key, {});
  const toggle = (i) => setSelected({ ...selected, [i]: !selected[i] });
  return (
    <div style={{margin:"18px 0",padding:"16px 18px",background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:12}}>
      <div style={{fontSize:13.5,fontWeight:700,color:C.text,marginBottom:12,lineHeight:1.5}}>{prompt}</div>
      <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
        {options.map((opt, i) => {
          const on = !!selected[i];
          return (
            <button key={i} onClick={() => toggle(i)}
              style={{padding:"8px 14px",borderRadius:20,fontSize:12.5,fontWeight:600,cursor:"pointer",
                border:`1px solid ${on?GOLD:C.border}`,
                background:on?GOLD:"transparent",
                color:on?"#111":C.textMid,
                transition:"all .15s"}}>{opt}</button>
          );
        })}
      </div>
    </div>
  );
}

function Cta({ label, url, note, C }) {
  return (
    <div style={{margin:"22px 0",padding:"18px 20px",borderRadius:14,border:`1px solid ${GOLD}55`,background:`linear-gradient(135deg, ${GOLD}14 0%, ${GOLD}04 100%)`}}>
      {note && <div style={{fontSize:13,color:C.textMid,lineHeight:1.6,marginBottom:12}}>{note}</div>}
      <a href={url} target="_blank" rel="noopener noreferrer"
        style={{display:"inline-flex",alignItems:"center",gap:8,padding:"12px 22px",borderRadius:10,background:GOLD,color:"#111",fontSize:14,fontWeight:800,textDecoration:"none",boxShadow:`0 4px 14px ${GOLD}44`,cursor:"pointer"}}>
        {label}
        <span style={{fontSize:14,lineHeight:1}}>→</span>
      </a>
    </div>
  );
}

function ActionSteps({ items, C }) {
  return (
    <div style={{display:"flex",flexDirection:"column",gap:10,margin:"18px 0"}}>
      {(items || []).map(asStep).map((it, i) => (
        <div key={i} style={{display:"flex",gap:14,padding:"14px 16px",background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:12,alignItems:"flex-start"}}>
          <div style={{flexShrink:0,width:28,height:28,borderRadius:"50%",background:`${GOLD}20`,border:`1px solid ${GOLD}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:GOLD}}>{i+1}</div>
          <div style={{minWidth:0}}>
            <div style={{fontSize:14,fontWeight:800,color:C.text,marginBottom:it.body?4:0}}>{it.title}</div>
            {it.body ? <div style={{fontSize:13,color:C.textMid,lineHeight:1.6}}>{it.body}</div> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function Recap({ id, title, module, moduleKey, C }) {
  // Collect all stored answers across chapters and summarize.
  const answers = useMemo(() => {
    const out = [];
    for (const ch of module.chapters) {
      for (const b of ch.blocks) {
        if (b.kind === "multipick") {
          const k = storageKey(moduleKey, `pick_${ch.id}_${b.id}`);
          try {
            const picks = JSON.parse(localStorage.getItem(k) || "{}");
            const labels = b.options.filter((_, i) => picks[i]);
            if (labels.length) out.push({ kind:"pick", prompt: b.prompt, labels });
          } catch {}
        } else if (b.kind === "assess") {
          const k = storageKey(moduleKey, `assess_${ch.id}_${b.id}`);
          try {
            const v = JSON.parse(localStorage.getItem(k) || "0");
            if (v) out.push({ kind:"assess", prompt: b.prompt, value: v, lowLabel: b.lowLabel, highLabel: b.highLabel });
          } catch {}
        } else if (b.kind === "checklist") {
          const k = storageKey(moduleKey, `check_${ch.id}_${b.id}`);
          try {
            const picks = JSON.parse(localStorage.getItem(k) || "{}");
            const labels = b.items.filter((_, i) => picks[i]);
            if (labels.length) out.push({ kind:"check", prompt: b.title, labels });
          } catch {}
        }
      }
    }
    return out;
    // eslint-disable-next-line
  }, [moduleKey]);

  return (
    <div style={{margin:"22px 0 8px",padding:"22px 22px",background:`linear-gradient(180deg, ${GOLD}12, ${GOLD}04)`,border:`1px solid ${GOLD}55`,borderRadius:14}}>
      <div style={{fontSize:11,fontWeight:800,color:GOLD,letterSpacing:1,marginBottom:6}}>RECAP</div>
      <div style={{fontSize:20,fontWeight:800,color:C.text,marginBottom:16}}>{title}</div>
      {answers.length === 0 ? (
        <div style={{fontSize:13,color:C.textMid,lineHeight:1.6}}>Go back through the chapters and mark what applies to your program. Your answers will show up here as a personalized summary.</div>
      ) : (
        <>
          {answers.map((a, i) => (
            <div key={i} style={{marginBottom:14}}>
              <div style={{fontSize:12,fontWeight:700,color:C.textDim,textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}>{a.prompt}</div>
              {a.kind === "assess" ? (
 <div style={{fontSize:14,color:C.text}}>Rated <strong style={{color:GOLD}}>{a.value}/5</strong>: {a.value <= 2 ? (a.lowLabel||"Low") : a.value >= 4 ? (a.highLabel||"High") : "Middle of the road"}</div>
              ) : (
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {a.labels.map((l, j) => (
                    <div key={j} style={{fontSize:12,fontWeight:600,color:"#111",background:GOLD,padding:"4px 10px",borderRadius:14}}>{l}</div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// Drill collection block — surfaces a list of existing drills from the
// regular drills library inside a masterclass chapter. Takes an array of
// drill titles (the same names visible in the admin Content Manager) and
// resolves them to rows from the `drills` table. Renders a grid of mini
// cards with the Mux auto-thumbnail; tap a card to play the drill in place
// (iframe swap, same pattern as VideoBlock).
//
// Titles are matched with case-insensitive exact match. Any title in the
// list that doesn't resolve to a drill row is logged to the console — the
// masterclass author (and admin) can see and fix the mismatch.

// Module-level cache keyed by lowercase title. Two drill collections in the
// same chapter (or two visits to the same chapter) get instant cache hits
// instead of re-querying Supabase. `null` = looked up, no row found (so we
// don't keep retrying); a row object = the actual drill data; absent key =
// not yet looked up. `_inflight` dedups concurrent requests for the same
// title so a chapter with 3 collections fires one query, not three.
const _drillCache = new Map();
let _drillsInflight = null;

// Cache/compare key for a drill title. Authored masterclass titles and the
// `drills.title` rows they point at drift apart in ways that don't change
// the name — casing, a stray trailing space, a double space between words.
// Keying on the raw string made any of those read as "drill not found" and
// blanked the whole collection (Paul, 2026-08-26: "there are no videos in
// the class"), so every lookup normalizes both sides.
const normTitle = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, " ");

async function fetchDrillsByTitles(titles) {
  const needed = titles.filter((t) => !_drillCache.has(normTitle(t)));
  if (needed.length === 0) return;
  // Coalesce with any in-flight batch — first caller wins, rest await.
  if (_drillsInflight) {
    await _drillsInflight.catch(() => {});
    return fetchDrillsByTitles(titles);
  }
  _drillsInflight = (async () => {
    const { data, error } = await supabase
      .from("drills")
      .select("id, title, duration, mux_playback_id")
      .in("title", needed)
      .not("mux_playback_id", "is", null);
    // Never let a failed request poison the cache. `error` used to be
    // discarded here, so a transient outage cached every title as a
    // permanent miss and silently emptied every drill collection for the
    // rest of the session. Throw so the caller can retry instead.
    if (error) throw error;
    const rows = data || [];
    const found = new Set();
    rows.forEach((r) => {
      _drillCache.set(normTitle(r.title), r);
      found.add(normTitle(r.title));
    });
    // `.in()` is an exact, case-sensitive match, so a row titled "Curry
    // drill" never matches an authored "Curry Drill". Retry each miss with
    // a case-insensitive match (ilike, no wildcards) before writing it off.
    // Bounded so a genuinely unwritten collection can't fan out into a
    // query storm.
    const misses = needed.filter((t) => !found.has(normTitle(t)));
    if (misses.length > 0 && misses.length <= 12) {
      const retried = await Promise.all(misses.map((t) => (
        supabase
          .from("drills")
          .select("id, title, duration, mux_playback_id")
          .ilike("title", t.trim())
          .not("mux_playback_id", "is", null)
          .limit(1)
          .then(
            (r) => ({ t, row: (r.data || [])[0] || null, failed: !!r.error }),
            () => ({ t, row: null, failed: true }),
          )
      )));
      if (retried.some((r) => r.failed)) throw new Error("drill lookup failed");
      retried.forEach(({ t, row }) => {
        if (!row) return;
        _drillCache.set(normTitle(t), row);
        found.add(normTitle(t));
      });
    }
    // Cache misses as `null` so a re-render doesn't trigger another query.
    needed.forEach((t) => {
      if (!found.has(normTitle(t))) _drillCache.set(normTitle(t), null);
    });
    // Warn in production too. These fire once per batch (misses are cached),
    // and a silent miss is exactly how an entire masterclass shipped with no
    // videos and nobody noticed until a member emailed.
    const stillMissing = needed.filter((t) => _drillCache.get(normTitle(t)) == null);
    if (stillMissing.length > 0) {
      // eslint-disable-next-line no-console
      console.warn("[DrillCollection] no matching published drill for:", stillMissing);
    }
  })();
  try { await _drillsInflight; } finally { _drillsInflight = null; }
}

function DrillCollection({ titles, title, subtitle, C }) {
  // Initial state: if every title is already cached, render synchronously
  // with no loading flash. Otherwise show the skeleton until the batch
  // resolves.
  const initialReady = Array.isArray(titles) && titles.length > 0
    && titles.every((t) => _drillCache.has(normTitle(t)));
  const [drills, setDrills] = useState(() => {
    if (!initialReady) return [];
    return titles.map((t) => _drillCache.get(normTitle(t))).filter(Boolean);
  });
  const [loading, setLoading] = useState(!initialReady);
  const [playingId, setPlayingId] = useState(null);
  // A failed lookup is NOT an empty collection. Tracked separately so the
  // reader sees "couldn't load, retry" instead of silence, and so one bad
  // request doesn't read as "this masterclass has no videos".
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!Array.isArray(titles) || titles.length === 0) { setLoading(false); return; }
    let cancelled = false;
    let retryTimer = null;
    (async () => {
      try {
        await fetchDrillsByTitles(titles);
        if (cancelled) return;
        const rows = titles.map((t) => _drillCache.get(normTitle(t))).filter(Boolean);
        setDrills(rows);
        setFailed(false);
      } catch (e) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn("[DrillCollection] fetch failed:", e && e.message);
        setFailed(true);
        // One automatic retry covers the common case (a single dropped
        // request); after that it's on the reader so we don't hammer a
        // struggling backend.
        if (attempt === 0) retryTimer = setTimeout(() => setAttempt(1), 1500);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; if (retryTimer) clearTimeout(retryTimer); };
  }, [titles, attempt]);

  // Stable order: preserve the order in the `titles` array so the masterclass
  // author can curate sequence. Drills not in the array get dropped above;
  // here we sort the survivors back into authoring order.
  const ordered = useMemo(() => {
    const byTitle = {};
    drills.forEach((d) => { byTitle[normTitle(d.title)] = d; });
    return (titles || []).map((t) => byTitle[normTitle(t)]).filter(Boolean);
  }, [drills, titles]);

  return (
    <div className="mc-drill-coll" style={{
      margin: "28px 0",
      padding: "32px 28px",
      borderRadius: 18,
      background: "linear-gradient(180deg, #0A0A0A 0%, #050505 100%)",
      border: `1px solid ${GOLD}22`,
      boxShadow: `inset 0 1px 0 ${GOLD}11, 0 20px 60px rgba(0,0,0,0.45)`,
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Top gold rule + label — sets the cinematic/marquee tone */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
        <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent, ${GOLD}55, transparent)` }} />
        <div style={{ fontSize: 10, fontWeight: 800, color: GOLD, letterSpacing: 3, textTransform: "uppercase" }}>
          On the floor
        </div>
        <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent, ${GOLD}55, transparent)` }} />
      </div>
      {title && (
        <div className="mc-drill-coll-title" style={{
          fontFamily: "'Bebas Neue', Impact, sans-serif",
          fontSize: 38,
          color: "#F5F0E8",
          letterSpacing: 3,
          textAlign: "center",
          lineHeight: 1.05,
          textShadow: `0 0 24px ${GOLD}22`,
          marginBottom: 6,
        }}>
          {title.toUpperCase()}
        </div>
      )}
      {subtitle && (
        <div className="mc-drill-coll-subtitle" style={{
          fontSize: 12,
          color: "#A09A8B",
          textAlign: "center",
          fontStyle: "italic",
          letterSpacing: 0.4,
          marginBottom: 28,
          maxWidth: 480,
          marginLeft: "auto",
          marginRight: "auto",
          lineHeight: 1.5,
        }}>
          {subtitle}
        </div>
      )}
      {loading ? (
        // Skeleton grid — matches the real card layout so the chapter
        // doesn't jump when the data arrives. Three placeholder cards
        // with a slow shimmer so the area reads as "content loading"
        // not "stuck spinner". titles.length is our best guess at how
        // many real cards we'll end up with; falls back to 3 if not set.
        <>
          <style>{`
            @keyframes mcDrillShimmer {
              0% { background-position: -240px 0; }
              100% { background-position: 240px 0; }
            }
            .mc-drill-skel {
              background: linear-gradient(90deg, rgba(255,255,255,0.025) 0%, rgba(226,221,159,0.06) 50%, rgba(255,255,255,0.025) 100%);
              background-size: 240px 100%;
              animation: mcDrillShimmer 1.4s ease-in-out infinite;
              border-radius: 6px;
            }
            @media (prefers-reduced-motion: reduce) {
              .mc-drill-skel { animation: none; background: rgba(255,255,255,0.04); }
            }
          `}</style>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16 }}>
            {Array.from({ length: Math.max(1, Math.min((titles || []).length || 3, 4)) }).map((_, i) => (
              <div key={i} style={{
                background: "#0F0F0F",
                border: `1px solid ${GOLD}1A`,
                borderRadius: 12,
                overflow: "hidden",
                boxShadow: `0 8px 24px rgba(0,0,0,0.5), inset 0 1px 0 ${GOLD}08`,
              }}>
                <div className="mc-drill-skel" style={{ width: "100%", aspectRatio: "16 / 9", borderRadius: 0 }}/>
                <div style={{ padding: "14px 16px 16px" }}>
                  <div className="mc-drill-skel" style={{ height: 14, width: "80%", marginBottom: 8 }}/>
                  <div className="mc-drill-skel" style={{ height: 9, width: "40%" }}/>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : failed && ordered.length === 0 ? (
        <div style={{ fontSize: 13, color: C.textDim, padding: "20px 0", textAlign: "center" }}>
          <div style={{ marginBottom: 12 }}>Couldn't load the clips for this section.</div>
          <button
            onClick={() => { setLoading(true); setAttempt((n) => n + 1); }}
            style={{
              padding: "8px 20px", borderRadius: 8, border: `1px solid ${GOLD}55`,
              background: "transparent", color: GOLD, fontSize: 12, fontWeight: 800,
              cursor: "pointer", fontFamily: "inherit", letterSpacing: 0.6,
            }}>
            RETRY
          </button>
        </div>
      ) : ordered.length === 0 ? (
        <div style={{ fontSize: 13, color: C.textDim, padding: "20px 0", textAlign: "center", fontStyle: "italic" }}>
          Clips for this section are on the way.
        </div>
      ) : (
        // Layout: a grid auto-filling 220px columns. When there's only
        // one card, the grid stretches it across the row which looks
        // off-balance (left-anchored, oversized thumbnail). Center it
        // and cap the width so it reads as deliberate, not a 1-of-N.
        <div style={{
          display: "grid",
          gridTemplateColumns: ordered.length === 1 ? "minmax(220px, 480px)" : "repeat(auto-fill, minmax(220px, 1fr))",
          gap: 16,
          justifyContent: ordered.length === 1 ? "center" : "stretch",
        }}>
          {ordered.map((d) => {
            const isPlaying = playingId === d.id;
            return (
              <div key={d.id} style={{
                background: "#0F0F0F",
                border: `1px solid ${GOLD}1A`,
                borderRadius: 12,
                overflow: "hidden",
                boxShadow: `0 8px 24px rgba(0,0,0,0.5), inset 0 1px 0 ${GOLD}08`,
                transition: "transform .2s ease, border-color .2s ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${GOLD}55`; e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = `${GOLD}1A`; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <div style={{ position: "relative", width: "100%", aspectRatio: "16 / 9", background: "#000" }}>
                  {isPlaying ? (
                    <iframe
                      title={d.title}
                      src={`https://player.mux.com/${d.mux_playback_id}?accent-color=${encodeURIComponent(GOLD)}&autoplay=1`}
                      allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                      allowFullScreen
                      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
                    />
                  ) : (
                    <div onClick={() => setPlayingId(d.id)} style={{ position: "absolute", inset: 0, cursor: "pointer" }}>
                      <img
                        src={`https://image.mux.com/${d.mux_playback_id}/thumbnail.jpg?width=480`}
                        alt={d.title}
                        loading="lazy"
                        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                      />
                      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(transparent 40%, rgba(0,0,0,0.7))" }} />
                      <div style={{
                        position: "absolute", top: "50%", left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 48, height: 48, borderRadius: "50%",
                        background: GOLD,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: `0 6px 20px ${GOLD}55, 0 0 0 6px rgba(0,0,0,0.35)`,
                      }}>
                        <Play size={18} color="#111" fill="#111" style={{ marginLeft: 2 }}/>
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ padding: "14px 16px 16px" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#F5F0E8", lineHeight: 1.35, letterSpacing: 0.2 }}>{d.title}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Worksheet — a structured fillable form rendered inline in a chapter.
// Used for the Card 5 player profile (and any future "fill this out as
// you read" pattern). Each section has a title + a list of fields. A
// field is either a plain text input row (just a label + an underline)
// or a list of indented sub-rows. Pure visual — nothing is persisted,
// the worksheet is print-and-fill or screenshot-and-share. The shape
// signals "you should literally do this exercise" in a way the prior
// callout-block version couldn't.
function Worksheet({ title, sections, C }) {
  const Field = ({ label, hint }) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{
        display: "flex", alignItems: "baseline", gap: 8,
        fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 4,
      }}>
        <span>{label}</span>
        {hint && <span style={{ fontSize: 10, fontWeight: 500, color: C.textDim }}>{hint}</span>}
      </div>
      <div style={{ height: 1, background: `${GOLD}44`, borderRadius: 1 }}/>
    </div>
  );
  return (
    <div style={{
      margin: "20px 0",
      borderRadius: 14,
      border: `1px solid ${GOLD}55`,
      background: `linear-gradient(180deg, rgba(226,221,159,0.05), rgba(226,221,159,0.01))`,
      overflow: "hidden",
    }}>
      {title && (
        <div style={{
          padding: "12px 20px",
          borderBottom: `1px solid ${GOLD}30`,
          fontSize: 11, fontWeight: 800, color: GOLD, letterSpacing: 1.8, textTransform: "uppercase",
          background: "rgba(226,221,159,0.06)",
        }}>{title}</div>
      )}
      <div style={{ padding: "18px 22px" }}>
        {(sections || []).map((sec, si) => (
          <div key={si} style={{ marginBottom: si === sections.length - 1 ? 0 : 18 }}>
            {sec.label && (
              <div style={{
                fontSize: 10, fontWeight: 800, color: GOLD, letterSpacing: 1.5,
                textTransform: "uppercase", marginBottom: 10,
              }}>{sec.label}</div>
            )}
            {(sec.fields || []).map((f, fi) => (
              <Field key={fi} label={f.label} hint={f.hint}/>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// Deck — sub-cards inside a single chapter/part. Click Next/Previous
// (or the dot indicator) to advance through cards without leaving the
// chapter. Each card has its own optional title + array of nested
// blocks, rendered by the same renderBlock for symmetry. Useful when
// a single concept has N parallel sub-concepts (e.g. a list of
// qualities, a checklist of frameworks, etc.) and you want each one
// to land on its own beat.
function Deck({ cards, ctx }) {
  const [idx, setIdx] = React.useState(0);
  const total = cards.length;
  const card = cards[idx];
  // Key off card.title so changing card resets internal state of any
  // nested interactive block. When advancing cards, scroll back to the
  // TOP of the deck card so the coach lands at "X of 4" instead of
  // staying parked mid-scroll on the previous card's content. Using
  // `block: "start"` (not "nearest") guarantees the jump-to-top, which
  // is what Coleman asked for: each card opens at its header.
  const containerRef = React.useRef(null);
  React.useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [idx]);
  const { C } = ctx;
  return (
    <div ref={containerRef} className="mc-deck-card" style={{
      position: "relative",
      margin: "20px 0",
      // Offset the smooth scroll-to-top so the card header doesn't land under
      // the reader's fixed top bar + progress strip (+ notch on mobile).
      scrollMarginTop: "calc(96px + env(safe-area-inset-top,0px))",
      // Layered gradient: dim diagonal base + gold corner glow up top.
      // Reads as a real surface, not a placeholder box.
      background: `
        radial-gradient(120% 80% at 0% 0%, rgba(226,221,159,0.10) 0%, rgba(226,221,159,0) 55%),
        radial-gradient(80% 60% at 100% 100%, rgba(226,221,159,0.06) 0%, rgba(226,221,159,0) 70%),
        linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)
      `,
      border: `1px solid ${GOLD}33`,
      borderRadius: 16,
      overflow: "hidden",
      // Outer glow + inset highlight so the card lifts off the page.
      boxShadow: `0 18px 40px rgba(0,0,0,0.55), 0 0 0 1px ${GOLD}10, inset 0 1px 0 rgba(255,255,255,0.04)`,
    }}>
      {/* Animated gold sweep along the top edge. Slow + subtle; the
          motion is enough to make the card feel alive without competing
          for attention from the content inside. */}
      <style>{`
        @keyframes mcDeckSweep {
          0%   { transform: translateX(-100%); opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { transform: translateX(100%); opacity: 0; }
        }
        .mc-deck-card::before {
          content: "";
          position: absolute; top: 0; left: 0; right: 0; height: 2px;
          background: linear-gradient(90deg, transparent 0%, ${GOLD} 50%, transparent 100%);
          animation: mcDeckSweep 6s ease-in-out infinite;
          pointer-events: none;
          z-index: 2;
        }
        @media (prefers-reduced-motion: reduce) {
          .mc-deck-card::before { animation: none; opacity: 0.3; }
        }
      `}</style>
      {/* Card header — index + total */}
      <div style={{
        position: "relative",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 20px",
        borderBottom: `1px solid ${GOLD}22`,
        background: "linear-gradient(180deg, rgba(226,221,159,0.10) 0%, rgba(226,221,159,0.02) 100%)",
      }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: GOLD, letterSpacing: 1.5,
          textTransform: "uppercase", whiteSpace: "nowrap", flexShrink: 0 }}>
          {idx + 1} of {total}
        </div>
        {/* `btn-inline` opts these out of the 44px minimum that dashboard.css
            forces on every button under 700px. That rule is right for real
            controls and catastrophic here: nine 6px dots each became 44px
            wide, so the row was 396px on a 390px screen and squeezed "2 of 9"
            into three stacked lines with giant circles beside it. The dots
            keep a 20px hit area of their own, which is the honest trade for
            a pager whose primary control is the Next button below it. */}
        <div style={{ display: "flex", gap: 1, flexShrink: 1, minWidth: 0,
          justifyContent: "flex-end", flexWrap: "nowrap" }}>
          {cards.map((_, i) => (
            <button
              key={i}
              className="btn-inline"
              onClick={() => setIdx(i)}
              aria-label={`Go to card ${i + 1}`}
              aria-current={i === idx}
              style={{
                width: 20, height: 20, minWidth: 20, minHeight: 20,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "transparent", border: "none", cursor: "pointer", padding: 0,
                flexShrink: 0,
              }}>
              <span style={{
                display: "block", width: 6, height: 6, borderRadius: "50%",
                background: i === idx ? GOLD : (C.border || "rgba(255,255,255,0.18)"),
              }}/>
            </button>
          ))}
        </div>
      </div>
      {/* Card body */}
      <div key={idx} style={{ padding: "20px 22px", animation: "deckFade .22s ease both" }}>
        <style>{`@keyframes deckFade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
        {card.title && (() => {
          // Optional icon prefix. Card config can pass `icon: "compass"`
          // and we render the matching Lucide icon at 24px gold.
          const IconCmp = card.icon ? DECK_ICONS[card.icon] : null;
          return (
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              marginBottom: 14, lineHeight: 1.25,
            }}>
              {IconCmp && (
                <div style={{
                  flexShrink: 0,
                  width: 36, height: 36, borderRadius: 9,
                  background: `${GOLD}18`, border: `1px solid ${GOLD}55`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <IconCmp size={18} color={GOLD}/>
                </div>
              )}
              <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{card.title}</div>
            </div>
          );
        })()}
        {(card.blocks || []).map((b, i) => (
          <React.Fragment key={i}>
            {renderBlock(b, ctx)}
          </React.Fragment>
        ))}
      </div>
      {/* Card nav */}
      <div style={{ display: "flex", gap: 10, padding: "12px 18px", borderTop: `1px solid ${C.cardBorder || C.border}` }}>
        <button
          onClick={() => setIdx(i => Math.max(0, i - 1))}
          disabled={idx === 0}
          style={{
            flex: 1, padding: "10px 0", borderRadius: 8,
            fontSize: 12, fontWeight: 700, fontFamily: "inherit",
            cursor: idx === 0 ? "default" : "pointer",
            border: `1px solid ${C.border}`, background: "transparent",
            color: idx === 0 ? C.textDim : C.textMid,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            opacity: idx === 0 ? 0.5 : 1,
          }}>
          <ChevronLeft size={14}/> Previous
        </button>
        <button
          onClick={() => setIdx(i => Math.min(total - 1, i + 1))}
          disabled={idx === total - 1}
          style={{
            flex: 1, padding: "10px 0", borderRadius: 8,
            fontSize: 12, fontWeight: 700, fontFamily: "inherit",
            cursor: idx === total - 1 ? "default" : "pointer",
            border: "none", background: idx === total - 1 ? `${GOLD}33` : GOLD,
            color: "#111",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            opacity: idx === total - 1 ? 0.6 : 1,
          }}>
          Next <ChevronRight size={14}/>
        </button>
      </div>
    </div>
  );
}

function renderBlock(block, ctx) {
  const { module, moduleKey, chapterId, C, dark } = ctx;
  // Modules with `layout: "article"` restyle a handful of kinds and fall
  // through for everything else. See ArticleBlocks.jsx.
  if (ctx.article) {
    const el = articleBlock(block, ctx);
    if (el !== undefined) return el;
  }
  switch (block.kind) {
    case "text": return <Paragraph md={block.md} C={C} dark={dark}/>;
    case "pullquote": return <PullQuote md={block.md} attribution={block.attribution} prominent={block.prominent} C={C}/>;
    case "worksheet": return <Worksheet title={block.title} sections={block.sections} C={C}/>;
    case "pgDiagnostic": return <PgDiagnostic C={C}/>;
    case "archetypeAnim": return <ArchetypeAnimation kind={block.archetype} C={C}/>;
    case "archetypeQuiz": {
      // Wire the quiz's "Run the full diagnostic" CTA to real chapter nav —
      // find whichever chapter carries the pgDiagnostic block (was a
      // window.alert telling the coach to go find it themselves).
      const diagIdx = (ctx.module?.chapters || []).findIndex(ch => (ch.blocks || []).some(bl => bl.kind === "pgDiagnostic"));
      return <ArchetypeQuiz C={C} onOpenDiagnostic={diagIdx >= 0 && ctx.goToChapter ? () => ctx.goToChapter(diagIdx) : undefined}/>;
    }
    case "heading": return <Heading level={block.level} text={block.text} C={C} id={ctx.headingId}/>;
    case "video": return <VideoBlock muxPlaybackId={block.muxPlaybackId} caption={block.caption} title={block.title} name={block.name} portrait={block.portrait} C={C}/>;
    case "callout": return <Callout tone={block.tone} title={block.title} body={block.body} C={C}/>;
    case "principles": return <Principles items={block.items} C={C}/>;
    case "compare": return <Compare {...block} C={C}/>;
    case "checklist": return <Checklist id={block.id} title={block.title} items={block.items} moduleKey={moduleKey} chapterId={chapterId} C={C}/>;
    case "assess": return <Assess id={block.id} prompt={block.prompt} lowLabel={block.lowLabel} highLabel={block.highLabel} moduleKey={moduleKey} chapterId={chapterId} C={C}/>;
    case "multipick": return <MultiPick id={block.id} prompt={block.prompt} options={block.options} moduleKey={moduleKey} chapterId={chapterId} C={C}/>;
    case "actionSteps": return <ActionSteps items={block.items} C={C}/>;
    case "cta": {
      // Adaptive CTAs — a block can set `mode: "public"` or
      // `mode: "signedIn"` to be hidden for the other audience. Used
      // on the end-of-masterclass CTAs where public visitors see a
      // signup pitch and signed-in coaches see an "explore the rest
      // of the platform" pointer. Unmoded CTAs render for everyone.
      if (block.mode === "public" && !ctx.publicMode) return null;
      if (block.mode === "signedIn" && ctx.publicMode) return null;
      return <Cta label={block.label} url={block.url} note={block.note} C={C}/>;
    }
    case "recap": return <Recap id={block.id} title={block.title} module={module} moduleKey={moduleKey} C={C}/>;
    case "drillCollection": return <DrillCollection titles={block.titles} title={block.title} subtitle={block.subtitle} C={C}/>;
    case "deck": return <Deck cards={block.cards} ctx={ctx}/>;
    case "image": return <ArticleImages images={block.images} C={C} dark={dark}/>;
    case "rule": return <SectionRule C={C} dark={dark}/>;
    case "graphic": return <BalanceGraphic name={block.name} caption={block.caption} C={C}/>;
    case "conceptGraphic": return <ConceptGraphic name={block.name} alt={block.alt} caption={block.caption} C={C}/>;
    case "table": return <ConceptTable C={C} head={block.head} rows={block.rows}/>;
    case "passGraphic": return <PassingGraphic name={block.name} caption={block.caption} active={block.active} C={C}/>;
    // Passing Accuracy has its own graphics and its own diagnostic. Separate
    // kinds rather than a shared one: the two masterclasses should be free to
    // move apart without one breaking the other.
    case "accuracyGraphic": return <AccuracyGraphic name={block.name} caption={block.caption} C={C} article={ctx.article} dark={dark}/>;
    case "passingMatrix": return <PassingMatrix C={C}/>;
    case "science": return <SciencePanel title={block.title} body={block.body} items={block.items} C={C}/>;
    /* ── Concept modules ──────────────────────────────────────────────
       Everything above `certDepth` is Platform-safe: nothing takes an
       answer, grades anything or blocks the next section. Everything from
       `certDepth` down asks the coach to do something, and belongs in the
       Certification version of a concept. */
    case "bigIdea": return <BigIdea C={C} md={block.md}/>;
    case "scenario": return <Scenario C={C} label={block.label} title={block.title} md={block.md} muxPlaybackId={block.muxPlaybackId}/>;
    case "bamInsight": return <BamInsight C={C} title={block.title} md={block.md}/>;
    case "framework": return <Framework C={C} title={block.title} steps={block.steps}/>;
    case "misconception": return <Misconception C={C} myth={block.myth} truth={block.truth}/>;
    case "reflection": return <Reflection C={C} title={block.title} md={block.md}/>;
    case "timedRounds": return <TimedRounds C={C} rounds={block.rounds}/>;
    case "masterclassLink": return <MasterclassLink C={C} moduleKey={block.key} title={block.title}/>;
    case "podcastLink": return <PodcastLink C={C} title={block.title} url={block.url}/>;
    case "certTeaser": return <CertTeaser C={C} certKey={block.certKey} enrolled={block.enrolled} admin={block.admin}/>;
    case "relatedConcepts": return <RelatedConcepts C={C} title={block.title} items={block.items} onOpen={ctx.onOpenConcept}/>;
    /* The two wrappers render their children through this same function, so
       anything that works at the top level works inside them. */
    case "certDepth": return (
      <CertDepth C={C} title={block.title}>
        {(block.blocks || []).map((b, i) => <React.Fragment key={i}>{renderBlock(b, ctx)}</React.Fragment>)}
      </CertDepth>
    );
    case "scienceNerd": return (
      <ScienceNerd C={C} title={block.title}>
        {(block.blocks || []).map((b, i) => <React.Fragment key={i}>{renderBlock(b, ctx)}</React.Fragment>)}
      </ScienceNerd>
    );
    case "research": return <ResearchCard C={C} {...block}/>;
    case "multipleChoice": return <MultipleChoice C={C} responses={ctx.responses} id={block.id} prompt={block.prompt} options={block.options} discussion={block.discussion}/>;
    case "writtenResponse": return <WrittenResponse C={C} responses={ctx.responses} id={block.id} prompt={block.prompt} hint={block.hint} discussion={block.discussion}/>;
    case "diagnostic": return <Diagnostic C={C} responses={ctx.responses} id={block.id} title={block.title} intro={block.intro} outro={block.outro} prompts={block.prompts} discussion={block.discussion}/>;
    case "applyToAthlete": return <ApplyToAthlete C={C} responses={ctx.responses} id={block.id} title={block.title} intro={block.intro} steps={block.steps} numbered={block.numbered !== false} discussion={block.discussion}/>;
    /* Prose with the reader's own paragraph rules, for use inside the two
       wrappers where a `text` block would also work but this keeps the
       nesting obvious. */
    case "prose": return <Prose C={C} md={block.md}/>;

    /* A set of drills that does not sort into a line. Tabs are the qualities
       being built; a drill can appear under more than one, and the second
       time you meet it the card says so. */
    case "skillTabs": return <SkillTabs C={C} dark={dark} intro={block.intro}
      qualities={block.qualities} intensity={block.intensity} posterTime={block.posterTime}
      echo={ctx.echo}
      fetchDrills={fetchDrillsByTitles} drillCache={_drillCache}/>;

    /* A whole masterclass as a guided journey: intro, stages, close. It
       draws its own head and completion, so its chapter is marked `bare`
       and the reader steps back. */
    case "journey": return <JourneyLayout block={block} C={C} dark={dark}
      completion={ctx.completion} publicMode={ctx.publicMode}
      fetchDrills={fetchDrillsByTitles} drillCache={_drillCache}/>;


    case "videoLessons": return <VideoLessons playbackId={block.playbackId} textTrackId={block.textTrackId} segments={block.segments} variant={block.variant} C={C} renderBlock={renderBlock} ctx={ctx}
      /* What the lock screen says while this plays. Only this block kind
         gets it: VideoLessons is the long-form surface (Weekly Q&A and
         talk-style masterclasses), and a short clip inside a chapter is
         not something anyone listens to with the screen off. */
      nowPlaying={{
        title: module?.title || "BAM Coaches",
        artist: module?.sub || "By Any Means Coaches",
        album: "BAM Coaches",
        artwork: block.playbackId
          ? `https://image.mux.com/${block.playbackId}/thumbnail.jpg?width=960&height=540&fit_mode=smartcrop`
          : undefined,
      }}/>;
    default: return null;
  }
}

/**
 * Pictures, in a row that becomes a column on a phone.
 *
 * Added for the two book covers at the end of the NBA article, which needed
 * to be seen rather than linked: a cover is the thing a reader recognises on
 * a shelf. An image with an `href` is a link, so the whole card is the
 * target rather than a small caption under it.
 */
function ArticleImages({ images, C, dark }) {
  const list = (images || []).filter(i => i && i.src);
  if (!list.length) return null;
  return (
    <div style={{display:"flex",gap:16,flexWrap:"wrap",margin:"28px 0",justifyContent:"center"}}>
      {list.map((img, i) => {
        const inner = (
          <>
            <img src={img.src} alt={img.alt || ""} loading="lazy"
              style={{display:"block",width:"100%",height:"auto",borderRadius:12,
                border:`1px solid ${C.border}`,
                boxShadow: dark ? "0 10px 36px rgba(0,0,0,0.5)" : "0 4px 18px rgba(0,0,0,0.12)"}}/>
            {img.caption && (
              <div style={{fontSize:12.5,color:C.textMid,marginTop:9,textAlign:"center",fontWeight:600}}>
                {img.caption}
              </div>
            )}
          </>
        );
        const box = {flex:"1 1 220px",maxWidth:300,minWidth:0,textDecoration:"none"};
        return img.href
          ? <a key={i} href={img.href} target="_blank" rel="noopener noreferrer" style={box}>{inner}</a>
          : <div key={i} style={box}>{inner}</div>;
      })}
    </div>
  );
}

/**
 * A section break with no words on it.
 *
 * The source article had a row of underscores after "Now for the good
 * stuff", marking the turn from the preamble into the ten recommendations.
 * It did not survive being read out of a PDF. A level 3 heading already
 * draws a divider, but that one carries a title, and this break is the
 * absence of one: the piece pauses, then starts properly.
 */
function SectionRule({ C, dark }) {
  return (
    <div aria-hidden="true" style={{ display: "flex", alignItems: "center",
      justifyContent: "center", gap: 10, margin: "34px 0 30px" }}>
      <span style={{ height: 1, flex: "0 1 120px",
        background: `linear-gradient(90deg, transparent, ${GOLD}${dark ? "55" : "77"})` }}/>
      <span style={{ width: 4, height: 4, borderRadius: "50%", background: GOLD, opacity: dark ? 0.75 : 0.9 }}/>
      <span style={{ height: 1, flex: "0 1 120px",
        background: `linear-gradient(90deg, ${GOLD}${dark ? "55" : "77"}, transparent)` }}/>
    </div>
  );
}

function PublicCtaPair({ C, size = "md" }) {
  const onSignup = (e) => {
    e?.preventDefault();
    window.location.hash = "#/signup";
  };
  const onLanding = (e) => {
    e?.preventDefault();
    window.location.hash = "#/landing";
  };
  const pad = size === "sm" ? "8px 14px" : "12px 22px";
  const fs = size === "sm" ? 12 : 14;
  return (
    <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
      <button onClick={onSignup}
        style={{padding:pad,borderRadius:10,background:GOLD,color:"#111",fontSize:fs,fontWeight:800,border:"none",cursor:"pointer",boxShadow:`0 4px 14px ${GOLD}44`}}>
        Sign up for full access
      </button>
      <button onClick={onLanding}
        style={{padding:pad,borderRadius:10,background:"transparent",color:C.text,fontSize:fs,fontWeight:700,border:`1px solid ${C.border}`,cursor:"pointer"}}>
        Read more
      </button>
    </div>
  );
}

export function MasterclassReader({ module, C, dark, onClose, onProgress, publicMode = false, onOpenPublicNav, onOpenConcept }) {
  const moduleKey = module.key;
  // Per-module opt in, so this lands on Passing Accuracy and nothing else.
  const isArticle = module.layout === "article";
  // Every masterclass uses the display face now, not just the article one,
  // so the font loads for all of them. `isArticle` still gates the LAYOUT.
  useArticleFont(true);

  // Page transition. `goPage` is what every navigation control calls in
  // article mode; `setActive` stays the raw setter for everything else.
  const [leaving, setLeaving] = useState(false);
  const pendingPage = useRef(null);
  const goPage = useCallback((next) => {
    const reduced = typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    setActive((cur) => {
      const target = typeof next === "function" ? next(cur) : next;
      if (target === cur) return cur;
      if (!isArticle || reduced) return target;
      // Hold the current page mounted so it can fade, then swap below.
      pendingPage.current = target;
      setLeaving(true);
      return cur;
    });
  }, [isArticle]);

  useEffect(() => {
    if (!leaving) return;
    const t = setTimeout(() => {
      if (pendingPage.current != null) setActive(pendingPage.current);
      pendingPage.current = null;
      setLeaving(false);
    }, 150);
    return () => clearTimeout(t);
  }, [leaving]);
  /* One row per coach per module, holding every written answer in it. Loaded
     once here rather than per block, so a Certification module with a
     diagnostic and four apply-to-your-player prompts is one read and one
     debounced write rather than nine of each. */
  const responses = useModuleResponses(moduleKey);
  const total = module.chapters.length;
  const isMobile = useIsMobile();

  // For chapters that lead with a video, fetch the real Mux runtime so the
  // header/TOC show the watch time instead of a word-count "read time".
  const [vidDur, setVidDur] = useState({});
  useEffect(() => {
    let cancelled = false;
    (module.chapters || []).forEach((ch) => {
      const pid = chapterVideoId(ch);
      if (!pid) return;
      fetchMuxDuration(pid).then((lbl) => {
        if (lbl && !cancelled) setVidDur((prev) => (prev[ch.id] ? prev : { ...prev, [ch.id]: lbl }));
      });
    });
    return () => { cancelled = true; };
  }, [module.key]);
  // A chapter may state its own length, and when it does it wins. The guess
  // below reads the FIRST video in a chapter and calls the whole thing a
  // watch, which is right for a lesson built around one clip and very wrong
  // for a 4,600 word article that happens to open with a three minute one.
  // A module can opt out of the length line entirely (hideReadTime). The
  // post-visit write-up is a private letter, not a lesson, so a "12 min read"
  // pill reads wrong on it; this returns "" and the render sites below skip
  // the element when the meta is empty.
  const chapterMeta = (ch) =>
    module.hideReadTime ? ""
    : ch.meta ? ch.meta
    : vidDur[ch.id] ? `${vidDur[ch.id]} watch`
    : `${readMinutes(ch)} min read`;

  // ── Immersive reading on a phone ──────────────────────────────────
  //
  // A third of the screen was chrome before a word of content: the app's
  // own bar on top of the reader's bar, plus four tabs at the bottom. The
  // tabs are all "leave this page", which is the one thing a reader should
  // not be invited to do, and nobody changes the theme mid-masterclass.
  //
  // Done with a body class and CSS rather than prop-drilling a flag up
  // through a 4000-line shell: the shell keeps rendering exactly what it
  // always did, and one media query hides it under 768px. Removed on
  // unmount and on an unexpected teardown, so the app can never be left
  // without navigation.
  useEffect(() => {
    document.body.classList.add("mc-immersive");
    return () => document.body.classList.remove("mc-immersive");
  }, []);



  const visitedKey = storageKey(moduleKey, "visited");
  const [visited, setVisited] = useLocal(visitedKey, {});
  // Last-active chapter index persists so coaches can resume where
  // they left off. We don't auto-jump there on mount (jumping users to
  // a non-zero index without consent feels disorienting). Instead we
  // surface a "Pick up at Chapter N" banner that they can click.
  const lastActiveKey = storageKey(moduleKey, "lastActive");
  const [lastActive, setLastActive] = useLocal(lastActiveKey, null);
  const [active, setActive] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Resume banner dismiss state. Hides for the rest of the session
  // once the coach either clicks Resume or dismisses it.
  const [resumeDismissed, setResumeDismissed] = useState(false);
  const scrollRef = useRef(null);

  // Persist last-active so we can offer a resume on next mount + reset
  // scroll on chapter change. NOTE: visited is no longer auto-set on
  // open — coaches mark a chapter complete explicitly via the Mark
  // Complete button or by clicking Next chapter, which makes the dot
  // strip an actual completion indicator rather than just "you've
  // opened this".
  useEffect(() => {
    setLastActive(active);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    // eslint-disable-next-line
  }, [active]);

  // Mark a chapter as complete. Used by the explicit Mark Complete
  // button + the Next chapter button. Idempotent.
  // Progress now lives on the account as well as in this browser, and each
  // completed page emits one analytics event. See useMasterclassProgress.js.
  useMasterclassProgress(moduleKey, visited, setVisited, total);
  // Views and read depth, for every piece this reader ever shows, public or
  // not. See useReadTracking.js.
  useReadTracking({ moduleKey, publicMode, layout: module.layout, active, total, scrollRef });

  const markChapterComplete = (chapterId) => {
    if (!chapterId || visited[chapterId]) return;
    const next = { ...visited, [chapterId]: true };
    setVisited(next);
    // Logged here rather than on render: paging past something is not a
    // read, and counting it would make the engagement numbers flattering
    // and useless.
    try {
      logMasterclassPage({
        moduleKey,
        chapterId,
        index: module.chapters.findIndex(c => c.id === chapterId),
        total: module.chapters.length,
        visitedCount: Object.keys(next).length,
      });
    } catch { /* analytics is never worth breaking a page for */ }
  };
  const toggleChapterComplete = (chapterId) => {
    if (!chapterId) return;
    const next = { ...visited };
    if (next[chapterId]) delete next[chapterId];
    else next[chapterId] = true;
    setVisited(next);
  };

  // True when there's a persisted last-active chapter > 0 that doesn't
  // match the current active (i.e. coach is re-opening at chapter 0
  // but had previously been on chapter N).
  const showResume = !resumeDismissed && active === 0 && lastActive != null && lastActive > 0 && lastActive < total;

  // Bubble progress upward so the card progress bar can reflect it.
  useEffect(() => {
    const completedCount = Object.keys(visited).filter(k => visited[k]).length;
    const pct = Math.min(100, Math.round((completedCount / total) * 100));
    if (onProgress) onProgress(pct);
  }, [visited, total, onProgress]);

  const chapter = module.chapters[active];
  // Page one of an article, when it is a section divider: it is the cover of
  // the piece, so it gets the standfirst rather than the section name. Guarded
  // on `sub` so an article module without one keeps the normal title.
  const isOpener = isArticle && active === 0 && !!chapter?.divider && !!module.sub;

  // Scroll position per page, for the session. Backing out of a 2000-word
  // page and returning used to dump the coach at the top to find their
  // place again. A ref rather than storage: within a session is where the
  // annoyance lives, and persisting it would fight the page transition on
  // a fresh open.
  /* How far down the page a reader is, 0 to 1.
     The bar under the header measured CHAPTERS, which is right for a
     fourteen chapter masterclass and meaningless for a one chapter
     article: (0+1)/1 is 100%, so a 4,600 word read opened with a full
     gold bar and never moved. Scroll is the honest measure when there is
     only one page, and on a longer piece it still refines the step rather
     than replacing it. */
  const [scrollPct, setScrollPct] = useState(0);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let raf = 0;
    const on = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const room = el.scrollHeight - el.clientHeight;
        setScrollPct(room > 40 ? Math.min(1, Math.max(0, el.scrollTop / room)) : 0);
      });
    };
    on();
    el.addEventListener("scroll", on, { passive: true });
    window.addEventListener("resize", on);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", on);
      window.removeEventListener("resize", on);
    };
  }, [active]);

  const scrollMemory = useRef({});
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const want = scrollMemory.current[active] || 0;
    // After paint, or the column has not been laid out yet and the
    // scrollTop is silently clamped to 0.
    const id = requestAnimationFrame(() => { el.scrollTop = want; });
    return () => {
      cancelAnimationFrame(id);
      scrollMemory.current[active] = el.scrollTop;
    };
  }, [active]);

  // Reader bar auto-hides scrolling down, returns scrolling up. The
  // Safari behaviour: full screen while reading, chrome one flick away.
  // Only below the first 80px, so a short page never loses its header.
  const [chromeHidden, setChromeHidden] = useState(false);
  const lastScroll = useRef(0);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const y = el.scrollTop;
      const dy = y - lastScroll.current;
      if (Math.abs(dy) < 6) return;          // ignore jitter and rubber-banding
      if (y < 80) setChromeHidden(false);
      else if (dy > 0) setChromeHidden(true);
      else setChromeHidden(false);
      lastScroll.current = y;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [active]);

  // Swipe to turn the page: the phone's version of the arrow keys below,
  // and the gesture people already expect from every reader app.
  //
  // Horizontal only, and only well clear of vertical: a thumb scrolling a
  // long page wanders sideways, and a swipe that fires on that would make
  // the piece feel like it is fighting you. Bows out of anything that
  // scrolls horizontally or plays video, via the same closest() opt-out
  // the drill stack uses, so the Mux scrubber keeps its own gestures.
  const swipe = useRef(null);
  const onSwipeStart = (e) => {
    if (e.touches.length !== 1) { swipe.current = null; return; }
    const t = e.touches[0];
    const inOptOut = e.target?.closest?.("iframe, [data-no-swipe], input, textarea, [contenteditable]");
    swipe.current = inOptOut ? null : { x: t.clientX, y: t.clientY, done: false };
  };
  const onSwipeMove = (e) => {
    const sw = swipe.current;
    if (!sw || sw.done) return;
    const t = e.touches[0];
    const dx = t.clientX - sw.x;
    const dy = t.clientY - sw.y;
    // Needs to be decisively sideways: 60px across and at least twice the
    // vertical drift.
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 2) return;
    sw.done = true;
    const fwd = dx < 0;                       // drag left = go forward
    if (fwd && active >= total - 1) return;
    if (!fwd && active <= 0) return;
    try { haptic("light"); } catch { /* web, or no native bridge */ }
    if (fwd) markChapterComplete(chapter.id);
    goPage(fwd ? active + 1 : active - 1);
  };
  const onSwipeEnd = () => { swipe.current = null; };

  // Left and right arrows page through the module.
  //
  // Bound to the document rather than to a focused element: a coach reading
  // never clicks the page first, so a handler that needed focus would do
  // nothing most of the time.
  //
  // It does exactly what the two buttons below do, including marking the
  // page complete on the way forward, so arrowing through a masterclass and
  // clicking through it leave the same progress behind.
  //
  // It bows out whenever the key plausibly belongs to something else: any
  // field or contenteditable (the discussion box, the worksheet inputs), any
  // modifier combo, since cmd/alt + arrow is browser history, and an open
  // video, where left and right are the player's own seek controls and
  // stealing them would be worse than not having this at all. Focus inside
  // the Mux iframe reads as the IFRAME element from out here, which is what
  // makes that last one checkable.
  useEffect(() => {
    const onKey = (e) => {
      const fwd = e.key === "ArrowRight";
      if (!fwd && e.key !== "ArrowLeft") return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const el = document.activeElement;
      const tag = el?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT"
          || tag === "IFRAME" || el?.isContentEditable) return;
      if (fwd && active >= total - 1) return;
      if (!fwd && active <= 0) return;
      e.preventDefault();
      if (fwd) markChapterComplete(chapter.id);
      goPage(fwd ? active + 1 : active - 1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // No dependency array on purpose. The handler closes over `active` and
    // the current chapter, and rebinding one listener per render is cheaper
    // and far harder to get wrong than keeping a dep list in step with it.
  });

  // Compute the in-chapter TOC items + scroll-progress tracking for the
  // currently-active chapter. Memoize the items so the hook's effect
  // doesn't re-run on every render (the hook's dep array is the headings
  // reference, which would change identity each render without memo).
  const tocItems = useMemo(() => chapterTOC(chapter), [chapter]);
  const { activeId, visitedIds } = useChapterProgress(tocItems, scrollRef);

  // Lead-magnet gate. Chapter 1 (index 0) is always free in publicMode.
  // Chapters 2+ require an email submission, persisted to localStorage
  // via hasCapturedLead/markLeadCaptured. The gate appears as a full-
  // screen overlay; submitting unlocks all chapters for the rest of the
  // session (and persistent across sessions on the same browser).
  const [leadCaptured, setLeadCaptured] = useState(() => hasCapturedLead(moduleKey));
  const needsGate = publicMode && active > 0 && !leadCaptured;

  // A module whose body is a set of drills can carry its framing as a pop-up
  // instead of a first chapter, so a coach coming back for a game on Tuesday
  // opens onto the games. Shown once, remembered, and reopenable from the
  // header, because "shown once" should not mean "gone".
  const [introOpen, setIntroOpen] = useState(
    () => !!module.intro && !introSeen(moduleKey)
  );
  const dismissIntro = () => { markIntroSeen(moduleKey); setIntroOpen(false); };

  // Module-level side panels (on this module: supplementing on air, and the
  // running order). They belong in the header rather than in a chapter: a
  // coach wants the regression at the moment a drill is not working, which
  // is any scroll position, and the running order once, before any of it.
  const [openPill, setOpenPill] = useState(null);
  const pills = module.pills || [];
  const pill = pills.find(p => p.id === openPill) || null;

  // One button, not three. Intro, the on-air panel and the running order were
  // three outlined chips sharing the top bar with the theme toggle and the
  // chapter counter, and three small things competing for attention lose to
  // one obvious thing every time.
  const [guideOpen, setGuideOpen] = useState(false);
  const guideItems = [
    ...(module.intro ? [{ id: "__intro", short: "Intro", label: "Intro",
      title: module.intro.title, blurb: "Why this is worth a session, and the four categories." }] : []),
    ...pills.map(p => ({ ...p, blurb: p.blurb || p.title })),
  ];
  const openGuideItem = (id) => {
    setGuideOpen(false);
    if (id === "__intro") setIntroOpen(true);
    else setOpenPill(id);
  };

  // The one panel worth repeating at the end of the drills, where a coach who
  // has watched them and is still stuck actually is.
  const echoPill = pills.find(p => p.id === module.echoPill) || null;

  const containerStyle = publicMode
    ? {position:"relative",flex:1,minWidth:0,height:"100%",background:dark?"#0A0A0A":"#FFFFFF",display:"flex",flexDirection:"column"}
    : {position:"fixed",inset:0,zIndex:4000,background:dark?"#0A0A0A":"#FFFFFF",display:"flex",flexDirection:"column"};

  // iOS-style edge swipe to go back: a rightward drag that starts near the
  // left edge closes the reader (back to the masterclass list). Only when the
  // chapter sidebar is closed and we're not in the public lead-magnet view.
  // Listeners are passive (no preventDefault) so normal scrolling is untouched.
  const swipeRef = useRef({ active: false, x0: 0, y0: 0 });
  const onTouchStart = (e) => {
    if (publicMode || !onClose || sidebarOpen) { swipeRef.current.active = false; return; }
    const t = e.touches[0];
    swipeRef.current = t && t.clientX <= 36 ? { active: true, x0: t.clientX, y0: t.clientY } : { active: false, x0: 0, y0: 0 };
  };
  const onTouchMove = (e) => {
    const s = swipeRef.current;
    if (s.active && Math.abs(e.touches[0].clientY - s.y0) > 60) s.active = false;
  };
  const onTouchEnd = (e) => {
    const s = swipeRef.current;
    if (!s.active) return;
    s.active = false;
    const t = e.changedTouches[0];
    if (t.clientX - s.x0 > 90 && Math.abs(t.clientY - s.y0) < 60) onClose();
  };

  return (
    <div className="mc-reader-enter" style={containerStyle}
      onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
      {/* Motion: reader rises in instead of hard-cutting over the grid;
          chapter changes cross-fade instead of snapping. */}
      <style>{`
        @keyframes mcReaderIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
        .mc-reader-enter { animation: mcReaderIn 0.22s cubic-bezier(0.2, 0.9, 0.3, 1) both; }
        @keyframes mcChapterIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        .mc-chapter-in { animation: mcChapterIn 0.2s ease-out both; }
        @keyframes mcPageOut { from { opacity: 1; transform: none; } to { opacity: 0; transform: translateY(8px); } }
        @keyframes mcPageIn  { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
        .mc-page-out { animation: mcPageOut .15s ease-in both; }
        .mc-page-in  { animation: mcPageIn .26s cubic-bezier(.22,.61,.36,1) both; }
        /* The headline leads; the body follows 60ms behind it. */
        .mc-page-in .mc-article-head { animation: mcPageIn .26s cubic-bezier(.22,.61,.36,1) both; }
        .mc-page-in .mc-article-body { animation: mcPageIn .3s cubic-bezier(.22,.61,.36,1) 60ms both; }
        @media (prefers-reduced-motion: reduce) {
          .mc-reader-enter, .mc-chapter-in, .mc-page-out, .mc-page-in,
          .mc-page-in .mc-article-head, .mc-page-in .mc-article-body { animation: none !important; }
        }
        @media (max-width: 640px) { .mc-article .mc-chapter-title { font-size: 33px !important; line-height: 1.1 !important; } }
        /* The opening standfirst is a sentence, not a title, so it does NOT
           want the title's phone size. At 33px it filled a whole iPhone
           screen before the Next button and read as a wall. */
        @media (max-width: 640px) {
          .mc-article .mc-standfirst { font-size: 21px !important; line-height: 1.36 !important; margin-bottom: 24px !important; }
        }
        ${ARTICLE_CSS}
      `}</style>
      {/* Lead-magnet email gate — overlays the whole reader when a
          public visitor tries to read past chapter 1 without
          submitting their email. Submitting marks the lead in
          localStorage and unlocks the rest of the masterclass. */}
      {guideOpen && (
        <div onClick={() => setGuideOpen(false)} role="dialog" aria-modal="true"
          aria-label="How to run this"
          style={{position:"fixed",inset:0,zIndex:5850,background:"rgba(0,0,0,0.62)",
            display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div onClick={e => e.stopPropagation()}
            style={{width:"100%",maxWidth:440,borderRadius:16,padding:"20px 22px 22px",
              background:dark?"#121212":"#FFFFFF",border:`1px solid ${C.border}`,
              fontFamily:"'DM Sans',sans-serif"}}>
            <div style={{fontSize:9.5,fontWeight:800,letterSpacing:1.4,textTransform:"uppercase",
              color:GOLD,marginBottom:12}}>How to run this</div>
            <div style={{display:"flex",flexDirection:"column",gap:7}}>
              {guideItems.map(it => (
                <button key={it.id} type="button" onClick={() => openGuideItem(it.id)}
                  style={{textAlign:"left",padding:"12px 14px",borderRadius:11,cursor:"pointer",
                    border:`1px solid ${C.border}`,background:"transparent",fontFamily:"inherit",
                    display:"block",width:"100%"}}>
                  <div style={{fontSize:14,fontWeight:800,color:C.text,marginBottom:3}}>
                    {it.label}
                  </div>
                  <div style={{fontSize:12.5,color:C.textDim,lineHeight:1.5}}>{it.blurb}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {pill && (
        <PillSheetModal item={pill} C={C} dark={dark} onClose={() => setOpenPill(null)}
          fetchDrills={fetchDrillsByTitles} drillCache={_drillCache}/>
      )}
      {module.intro && introOpen && (
        <ModuleIntro intro={module.intro} moduleKey={moduleKey} C={C} dark={dark}
          onDismiss={dismissIntro}/>
      )}
      {needsGate && (
        <MasterclassEmailGate
          moduleKey={moduleKey}
          moduleTitle={module.title}
          source={readUtmSource()}
          onUnlock={() => setLeadCaptured(true)}
          onBack={() => setActive(0)}
          C={C}
        />
      )}
      {/* Mobile-responsive overrides. Inline styles don't support media
          queries, so the reader's a few hot spots (reading column, chapter
          title, pullquote glyph + body, drill collection chrome, sticky
          mini-TOC, and sidebar behavior) are className-tagged below and
          tuned here for <= 640px / <= 768px. */}
      <style>{`
        @media (max-width: 640px) {
          .mc-reading-col {
            padding: 18px 16px 60px !important;
          }
          /* Public-preview reads have a sticky bottom CTA bar; give the
             scroll column extra clearance so last-chapter content doesn't
             tuck under it. */
          .mc-reading-col-public {
            padding-bottom: calc(60px + 72px + env(safe-area-inset-bottom)) !important;
          }
          .mc-chapter-title {
            font-size: 22px !important;
            margin: 0 0 14px !important;
          }
          .mc-pullquote-wrap {
            padding: 8px 4px 4px 42px !important;
            margin: 28px 0 24px !important;
          }
          .mc-pullquote-glyph {
            font-size: 78px !important;
            top: -8px !important;
          }
          .mc-pullquote-lead {
            font-size: 20px !important;
            line-height: 1.4 !important;
          }
          .mc-pullquote-rest {
            font-size: 14px !important;
          }
          .mc-drill-coll {
            padding: 22px 16px !important;
            margin: 22px 0 !important;
          }
          .mc-drill-coll-title {
            font-size: 26px !important;
            letter-spacing: 2px !important;
          }
          .mc-drill-coll-subtitle {
            font-size: 11px !important;
            margin-bottom: 20px !important;
          }
          .mc-sticky-mini-toc {
            max-width: 88vw !important;
          }
          .mc-sticky-mini-toc-title {
            max-width: 150px !important;
            font-size: 11px !important;
          }
          .mc-callout-tab {
            font-size: 9px !important;
            padding: 3px 7px 3px 6px !important;
            left: 12px !important;
          }
          .mc-section-divider {
            margin-bottom: 14px !important;
          }
          .mc-chapter-section {
            margin: 30px 0 12px !important;
          }
        }
        /* The reader's own bar slides away on scroll down and comes back
           on scroll up. Transform rather than display, so it animates and
           so the layout below never reflows mid-scroll. */
        .mc-topbar {
          transition: transform .22s cubic-bezier(.2,.8,.2,1);
          will-change: transform;
        }
        @media (max-width: 768px) {
          .mc-topbar.mc-topbar-hidden { transform: translateY(-100%); }
          /* Previous / Next pinned to the thumb. Replaces the inline pair
             that needed a scroll to the end of a 2000-word page to reach,
             and takes over the space the app's tab bar just vacated. */
          .mc-mobile-nav {
            position: fixed; left: 0; right: 0; bottom: 0; z-index: 3000;
            display: flex; gap: 10px;
            padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
            backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px);
          }
          /* Clear the fixed bar, or the last paragraph of every page sits
             underneath it. */
          .mc-article .mc-reading-col, .mc-reading-col {
            padding-bottom: calc(96px + env(safe-area-inset-bottom, 0px)) !important;
          }
          .mc-inline-nav { display: none !important; }
          /* Coleman sees the article slide side to side on his phone and I
             could not reproduce it at 320, 360, 375, 393 or 430 in a real
             browser. So this is a guard rather than a fix: nothing in a
             column of prose has any business scrolling sideways, and if
             something later does overflow, it clips instead of dragging the
             whole page off centre. The video blocks keep their full-bleed
             negative margins, since those are the parent's own padding. */
          .mc-article-body, .mc-article-head { overflow-x: hidden; }
          /* Video goes edge to edge. The player is the thing they came
             for and the 28px gutters were costing it ~15% of its width. */
          .mc-article .mc-video-wrap, .mc-video-wrap {
            margin-left: -28px !important; margin-right: -28px !important;
            border-radius: 0 !important;
          }
        }
        @media (max-width: 768px) {
          /* Sidebar becomes a full-height overlay instead of squeezing
             the main reading column to a sliver. Backdrop dims the
             content behind so the sidebar reads as a sheet. */
          .mc-sidebar {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            bottom: 0 !important;
            width: min(85vw, 320px) !important;
            z-index: 4500 !important;
            box-shadow: 12px 0 36px rgba(0,0,0,0.65) !important;
          }
          .mc-sidebar-backdrop {
            display: block !important;
          }
        }
      `}</style>

      {/* Top bar — pad for the iOS notch/status bar (viewport-fit=cover) so
          the eyebrow + title don't tuck under it on notched iPhones. */}
      {/* The reader's own bar is hidden on a public article, on Coleman's
          call: a free piece opens with an eyebrow, a repeat of the title it
          is already about to show, and two buttons, before a word of the
          thing somebody clicked through to read. Everything on it has
          somewhere better to be. The title is the headline a line below.
          Position is the progress bar. The conversion pair moves to the
          sticky bar at the bottom, which now runs on desktop too, so the
          ask is still always on screen without being the first thing. */}
      {!publicMode && (
      <div className={`mc-topbar${chromeHidden ? " mc-topbar-hidden" : ""}`}
        style={{display:"flex",alignItems:"center",gap:12,padding:isMobile?"10px 12px":"12px 18px",paddingTop:isMobile?"calc(10px + env(safe-area-inset-top,0px))":undefined,borderBottom:`1px solid ${C.border}`,background:dark?"#0E0E0E":"#FAFAFA"}}>
        {/* Back to the masterclass list. The clear, expected exit (top-left);
            replaces the easy-to-miss X that used to live top-right. */}
        {!publicMode && onClose && (
          <button onClick={onClose} aria-label="Back to masterclasses"
            style={{display:"flex",alignItems:"center",gap:5,background:"transparent",border:`1px solid ${C.border}`,borderRadius:8,cursor:"pointer",padding:isMobile?"0 10px":"6px 10px",minHeight:isMobile?40:undefined,color:C.text,flexShrink:0}}>
            <ChevronLeft size={isMobile?20:16} color={C.text}/>
            {!isMobile && <span style={{fontSize:13,fontWeight:700,color:C.text}}>Back</span>}
          </button>
        )}
        {/* In publicMode on mobile, the hamburger opens the locked nav
            (Dashboard / Drills / Plans / ...). Chapter jumping is handled
            by the dot-strip below — surfacing the conversion nav matters
            more on a lead-magnet view. */}
        <button
          onClick={() => {
            if (publicMode && isMobile && onOpenPublicNav) onOpenPublicNav();
            else setSidebarOpen(o => !o);
          }}
          aria-label={publicMode && isMobile ? "Open menu" : "Toggle chapters"}
          style={{background:"transparent",border:"none",cursor:"pointer",padding:isMobile?10:6,display:"flex",alignItems:"center",justifyContent:"center",minWidth:isMobile?44:undefined,minHeight:isMobile?44:undefined,marginLeft:isMobile?-6:0}}>
          <Menu size={isMobile?20:18} color={C.textMid}/>
        </button>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:11,fontWeight:700,color:GOLD,letterSpacing:1,textTransform:"uppercase"}}>{module.kind === "lesson" ? "Lesson" : module.category === "Concepts" ? "Scientific concept" : "Masterclass"}</div>
          {/* "Building Precise Passers: A Deep..." told nobody anything.
              On a phone the chapter is the useful label, and the page's
              own eyebrow already names the masterclass. Two lines before
              it clips, so most chapter titles land whole. */}
          <div style={{fontSize:14,fontWeight:800,color:C.text,lineHeight:1.25,
            display:"-webkit-box",WebkitLineClamp:isMobile?2:1,WebkitBoxOrient:"vertical",
            overflow:"hidden",wordBreak:"break-word"}}>
            {isMobile ? chapterDisplayTitle(chapter?.title) || module.title : module.title}
          </div>
        </div>
        {guideItems.length > 0 && !introOpen && (
          <button onClick={() => setGuideOpen(true)} aria-label="How to run this"
            style={{background:GOLD,border:"none",borderRadius:8,
              cursor:"pointer",padding:isMobile?"0 12px":"7px 13px",minHeight:isMobile?40:undefined,
              color:"#111",fontSize:12,fontWeight:800,flexShrink:0,fontFamily:"inherit",
              whiteSpace:"nowrap"}}>
            {isMobile ? "How to run" : "How to run this"}
          </button>
        )}
        {!publicMode && (
          <div style={{fontSize:12,color:C.textDim,fontWeight:600}}>
            {active+1} / {total}
          </div>
        )}
        {publicMode && !isMobile && <PublicCtaPair C={C} size="sm"/>}
      </div>
      )}

      {/* The per-chapter dot row that used to sit here is gone, on Coleman's
          call, for every masterclass rather than just the article one.
          On a 21 page piece it was 21 near-identical pills that read as
          decoration, and everything it did is still available and better
          said elsewhere: the moving bar below carries progress, the "n / m"
          in the header carries position, and the sidebar lists the chapters
          by name, which is what someone jumping around actually needs. */}
      <div style={{height:3,background:C.border,flexShrink:0}}>
        <div style={{height:"100%",background:GOLD,transition:"width .15s linear",
          width:`${Math.round(total > 1
            ? ((active + scrollPct) / total) * 100
            : scrollPct * 100)}%`}}/>
      </div>

      <div style={{flex:1,display:"flex",minHeight:0}}>
        {/* Sidebar — desktop renders inline as a fixed-width column.
            Mobile (<= 768px) flips it to a fixed overlay via the .mc-sidebar
            CSS overrides at the top of this component, with a backdrop
            element rendered below that closes the sidebar on tap. */}
        {sidebarOpen && (
          <>
            <div
              className="mc-sidebar-backdrop"
              onClick={() => setSidebarOpen(false)}
              style={{
                display: "none", // shown only on mobile via media query
                position: "fixed", inset: 0,
                background: "rgba(0,0,0,0.55)",
                zIndex: 4400,
              }}
            />
            <div className="mc-sidebar" style={{width:280,flexShrink:0,borderRight:`1px solid ${C.border}`,background:dark?"#0C0C0C":"#F8F8F8",overflow:"auto",padding:"14px 10px"}}>
            <div style={{fontSize:10,fontWeight:800,color:C.textDim,letterSpacing:1,textTransform:"uppercase",padding:"4px 10px 8px"}}>{module.kind === "lesson" ? "Pages" : "Chapters"}</div>
            {module.chapters.map((ch, i) => {
              const isActive = i === active;
              // A divider IS the section heading, so it labels the group
              // rather than sitting in it as another numbered row.
              if (ch.divider) return (
                <button key={ch.id} onClick={() => { goPage(i); setSidebarOpen(false); }}
                  style={{display:"block",width:"100%",textAlign:"left",padding:"16px 10px 6px",
                    border:"none",background:"transparent",cursor:"pointer",fontFamily:"inherit",
                    fontSize:10,fontWeight:800,letterSpacing:"0.13em",textTransform:"uppercase",
                    color:isActive?GOLD:C.textDim}}>
                  {ch.section}
                </button>
              );
              const isVisited = !!visited[ch.id];
              const mins = readMinutes(ch);
              return (
                <button key={ch.id} onClick={() => { goPage(i); setSidebarOpen(false); }}
                  style={{display:"flex",alignItems:"center",gap:10,width:"100%",textAlign:"left",padding:"10px 10px",borderRadius:8,border:"none",cursor:"pointer",
                    background:isActive?`${GOLD}14`:"transparent",
                    color:isActive?C.text:C.textMid,
                    marginBottom:2}}>
                  <div style={{flexShrink:0,width:20,height:20,borderRadius:"50%",border:`1.5px solid ${isVisited?GOLD:C.border}`,background:isVisited?GOLD:"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {isVisited ? <Check size={11} color="#111" strokeWidth={3}/> : <span style={{fontSize:10,fontWeight:800,color:C.textDim}}>{i+1}</span>}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:isActive?700:500,lineHeight:1.4}}>{chapterDisplayTitle(ch.title)}</div>
                    <div style={{fontSize:10,color:C.textDim,marginTop:2,fontFamily:"'JetBrains Mono', monospace"}}>{ch.meta ? ch.meta : vidDur[ch.id] ? `${vidDur[ch.id]} watch` : `${mins} min`}</div>
                  </div>
                </button>
              );
            })}
            </div>
          </>
        )}

        {/* Phone nav, pinned to the thumb. CSS shows this only under
            768px and hides the inline pair, so neither is ever duplicated
            on screen. Lives outside the scrolling column so it does not
            move, and so it stays reachable on a 2000-word page.

            Not on a one-page article: with nowhere to go, the bar was a
            gold "Finish" that only closed the page, sitting on top of the
            public sign-up bar besides. */}
        {isMobile && total > 1 && (
          <div className="mc-mobile-nav" style={{background:dark?"rgba(14,14,14,0.92)":"rgba(250,250,250,0.94)",borderTop:`1px solid ${C.border}`}}>
            {active > 0 && (
              <button onClick={() => { haptic("light"); goPage(a => Math.max(0, a-1)); }}
                aria-label="Previous page"
                style={{flex:"0 0 auto",minWidth:52,minHeight:46,borderRadius:10,cursor:"pointer",
                  border:`1px solid ${C.border}`,background:"transparent",color:C.textMid,
                  display:"flex",alignItems:"center",justifyContent:"center"}}>
                <ChevronLeft size={18}/>
              </button>
            )}
            <button
              onClick={() => {
                haptic("light");
                markChapterComplete(chapter.id);
                if (active < total - 1) goPage(a => Math.min(total-1, a+1));
                else onClose && onClose();
              }}
              style={{flex:1,minWidth:0,minHeight:46,padding:"0 14px",borderRadius:10,fontSize:13.5,fontWeight:800,
                cursor:"pointer",border:"none",background:GOLD,color:"#111",
                display:"flex",alignItems:"center",justifyContent:"center",gap:6,boxShadow:`0 4px 14px ${GOLD}44`}}>
              <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",minWidth:0}}>
                {active < total - 1
                  ? (chapterDisplayTitle(module.chapters[active + 1]?.title) || "Next")
                  : "Finish"}
              </span>
              {active < total - 1 && <ChevronRight size={16} style={{flexShrink:0}}/>}
            </button>
          </div>
        )}

        {/* Main reading column */}
        <div ref={scrollRef} style={{flex:1,overflow:"auto"}}
          onTouchStart={onSwipeStart} onTouchMove={onSwipeMove} onTouchEnd={onSwipeEnd}>
          <div key={active} className={`mc-reading-col ${isArticle ? (leaving ? "mc-page-out mc-article" : "mc-page-in mc-article") : "mc-chapter-in"}${publicMode ? " mc-reading-col-public" : ""}`}
            style={{maxWidth:isArticle?720:760,margin:"0 auto",padding:isArticle?"34px 28px 96px":"28px 28px 80px",
              // A short page used to leave several hundred pixels of dead
              // black under the button, which reads as something failing
              // to load. Filling the column means the page ends where the
              // screen does.
              minHeight:isMobile?"calc(100vh - 210px)":undefined,
              display:isMobile?"flex":undefined, flexDirection:isMobile?"column":undefined,
              // `safe` is what makes this usable: a short page (a divider,
              // the cover) centres, and a long one falls back to starting
              // at the top instead of centring and clipping its own
              // opening lines off the screen.
              justifyContent:isMobile?"safe center":undefined}}>
            {/* Resume banner — surfaces a "Pick up at Chapter N" CTA
                when a returning coach lands back on the masterclass at
                chapter 0 but had previously progressed further. We do
                NOT auto-jump them; the action is theirs. */}
            {showResume && (
              <div style={{
                marginBottom: 24, padding: "12px 16px", borderRadius: 10,
                background: `linear-gradient(180deg, ${GOLD}18, ${GOLD}06)`,
                border: `1px solid ${GOLD}55`,
                display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
              }}>
                <div style={{flex:1, minWidth: 200, fontSize: 13, color: C.text, lineHeight: 1.45}}>
                  Pick up at <strong style={{color: GOLD}}>
                    {module.kind === "lesson" ? "Page" : "Chapter"} {lastActive + 1}
 </strong>: {chapterDisplayTitle(module.chapters[lastActive]?.title)}
                </div>
                <div style={{display:"flex", gap: 8}}>
                  <button onClick={() => { setResumeDismissed(true); }}
                    style={{padding:"7px 14px",borderRadius:8,fontSize:12,fontWeight:700,background:"transparent",border:`1px solid ${C.border}`,color:C.textMid,cursor:"pointer"}}>
                    Start over
                  </button>
                  <button onClick={() => { setActive(lastActive); setResumeDismissed(true); }}
                    style={{padding:"7px 16px",borderRadius:8,fontSize:12,fontWeight:800,background:GOLD,border:"none",color:"#111",cursor:"pointer",display:"inline-flex",alignItems:"center",gap:4}}>
                    Resume <ChevronRight size={13}/>
                  </button>
                </div>
              </div>
            )}
            {/* A `bare` chapter is one whose block draws its own head (the
                journey layout), so the chapter number, length and title
                would only repeat it. */}
            {!chapter.bare && (
            <div className="mc-article-head">
            {/* The masthead names the piece above the chapter's own title,
                which earns its place on a fourteen chapter masterclass. On a
                one chapter article the chapter IS the piece, so it printed
                the headline in small caps and then printed it again, full
                size, two lines below. */}
            {isArticle && total > 1 && (
              <div style={{marginBottom:16}}>
                <div style={{
                  fontSize:10.5,fontWeight:800,letterSpacing:"0.17em",textTransform:"uppercase",
                  color:articleAccent(dark),
                }}>{module.title}</div>
                <div aria-hidden="true" style={{
                  height:1,width:104,marginTop:13,background:articleAccent(dark),
                  opacity:dark?0.55:0.75,
                }}/>
              </div>
            )}
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6,flexWrap:"wrap"}}>
              {!isArticle && (
                <div style={{fontSize:11,fontWeight:800,color:C.textDim,letterSpacing:1.2,textTransform:"uppercase"}}>
                  {module.kind === "lesson" ? "Page" : "Chapter"} {active+1}
                </div>
              )}
              {/* On an article with a byline the length belongs NEXT TO the
                  byline, the way every piece of writing sets it, rather than
                  floating on its own line above the headline. */}
              {(isArticle && chapter.divider) || (module.byline && active === 0) || !chapterMeta(chapter) ? null : (
              <div style={isArticle
                ? {fontSize:13,fontWeight:400,color:C.textDim,letterSpacing:"0.01em",fontStyle:"italic",fontFamily:ARTICLE_DISPLAY}
                : {fontSize:11,fontWeight:700,color:GOLD,padding:"2px 8px",borderRadius:10,background:`${GOLD}14`,fontFamily:"'JetBrains Mono', monospace"}}>
                {chapterMeta(chapter)}
              </div>
              )}
            </div>
            {/* The opening page is a cover, not a section divider. Leading it
                with the first section's name ("Why strike zone passes matter")
                answers a question the reader has not been asked yet, so the
                standfirst takes the headline slot and sells the thing, and the
                section name steps down to a label on the contents list below.
                Set smaller than a chapter title because it is a sentence, not
                a title, and 44px of sentence is a wall. */}
            <h2 className={`mc-chapter-title${isOpener ? " mc-standfirst" : ""}`} style={isArticle
              ? (isOpener
                ? {fontFamily:ARTICLE_DISPLAY,fontSize:30,fontWeight:600,color:C.text,
                   margin:"4px 0 30px",lineHeight:1.3,letterSpacing:"-0.013em",
                   fontVariationSettings:"'opsz' 60",maxWidth:"30ch"}
                : {fontFamily:ARTICLE_DISPLAY,fontSize:44,fontWeight:700,color:C.text,
                   // A byline sits under the headline, so the headline gives
                   // up its bottom margin to it rather than the byline being
                   // dragged up through it on a negative one.
                   margin:module.byline&&active===0?"4px 0 10px":"4px 0 28px",
                   lineHeight:1.07,letterSpacing:"-0.021em",
                   fontVariationSettings:"'opsz' 100"})
              // Same face for the card-layout masterclasses, a size down:
              // those pages carry an eyebrow pill and an "in this chapter"
              // card above the title, so 44px would shout over them.
              : {fontFamily:ARTICLE_DISPLAY,fontSize:34,fontWeight:700,color:C.text,
                 margin:"0 0 20px",lineHeight:1.14,letterSpacing:"-0.018em",
                 fontVariationSettings:"'opsz' 90"}}>
              {isOpener ? module.sub : chapterDisplayTitle(chapter.title)}</h2>
            {/* Whose piece this is. Under the headline, on the first page
                only: a byline repeated at the top of every chapter stops
                reading as authorship and starts reading as a header. */}
            {module.byline && active === 0 && (
              <div style={{display:"flex",alignItems:"baseline",gap:9,flexWrap:"wrap",
                margin:"0 0 36px"}}>
                <span style={{fontFamily:ARTICLE_DISPLAY,fontSize:15.5,fontWeight:500,
                  color:C.text,letterSpacing:"0.005em"}}>{module.byline}</span>
                <span aria-hidden="true" style={{color:C.textMid,fontSize:13,opacity:0.6}}>&middot;</span>
                {/* textMid, not textDim. In dark mode textDim is #555, which
                    over #0A0A0A is about 2:1 and read as a ghost of a line
                    rather than as the length of the piece. */}
                <span style={{fontFamily:ARTICLE_DISPLAY,fontSize:14.5,fontWeight:400,
                  fontStyle:"italic",color:C.textMid}}>{chapterMeta(chapter)}</span>
              </div>
            )}
            </div>
            )}

            <div className="mc-article-body">

            {/* Sticky mini-TOC pill removed at Coleman's request (2026-06-28) —
                felt like clutter, especially in light mode. The component
                (StickyMiniToc) is kept above in case we want it back. */}
            {/* In-chapter TOC for chapters with 3+ H3 headings. Now
                shows per-section progress dots. */}
            <ChapterTOC items={tocItems} activeId={activeId} visitedIds={visitedIds} C={C} article={isArticle}/>

            {chapter.blocks.map((b, i) => {
              // Pass a stable id for each heading so the in-chapter TOC
              // anchors can scroll to it.
              const headingId = b.kind === "heading" ? `h-${chapter.id}-${i}` : undefined;
              const Wrap = isArticle ? Reveal : React.Fragment;
              return (
                <Wrap key={i}>
                  {renderBlock(b, { module, moduleKey, chapterId: chapter.id, C, dark, headingId, publicMode, goToChapter: setActive, responses, onOpenConcept,
                    article: isArticle,
                    echo: echoPill ? { ...echoPill, onOpen: () => setOpenPill(echoPill.id) } : null,
                    // For blocks that own the page's close (the journey): the
                    // same completion toggle as the pill below, and a way to
                    // leave the reader for another part of the platform.
                    completion: publicMode ? null : {
                      done: !!visited[chapter.id],
                      // Marking goes through markChapterComplete so it logs the
                      // read the way Finish used to; unmarking is the toggle.
                      toggle: () => visited[chapter.id]
                        ? toggleChapterComplete(chapter.id)
                        : markChapterComplete(chapter.id),
                      leaveTo: (route) => { onClose && onClose(); window.location.hash = `#/${route}`; },
                    } })}
                </Wrap>
              );
            })}
            {/* Contents on a section divider, but NOT on the opening page.
                There the standfirst is doing the selling and a list of two
                page titles under it just delays the one action that matters,
                which is starting. The other dividers keep theirs: by then a
                coach is oriented and "what is in this section" is a real
                question. */}
            {isArticle && chapter.divider && !isOpener && (
              <SectionContents module={module} section={chapter.section}
                onGo={goPage} C={C} dark={dark}/>
            )}
            </div>

            {/* Mark Complete — explicit completion signal. Toggles
                visited state for the current chapter. Clicking Next
                or Finish below also auto-marks. */}
            {(() => {
              const isDone = !!visited[chapter.id];
              // Next already marks the page complete on the way out, so in an
              // article this pill is a second button for something that has
              // already happened. The toggle still exists for the other
              // twelve masterclasses, where the dot strip is the UI for it.
              if (isArticle) return null;
              // A bare chapter's block carries its own completion.
              if (chapter.bare) return null;
              return (
                <div style={{
                  display: "flex", justifyContent: "center",
                  marginTop: 32, paddingTop: 18,
                  borderTop: `1px solid ${C.border}`,
                }}>
                  <button onClick={() => toggleChapterComplete(chapter.id)}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 8,
                      padding: "10px 22px", borderRadius: 100,
                      fontSize: 12, fontWeight: 800,
                      letterSpacing: 0.5, textTransform: "uppercase",
                      cursor: "pointer",
                      border: `1px solid ${isDone ? GOLD : C.border}`,
                      background: isDone ? `${GOLD}18` : "transparent",
                      color: isDone ? GOLD : C.textMid,
                      transition: "all .15s",
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                    onMouseEnter={(e) => { if (!isDone) e.currentTarget.style.borderColor = `${GOLD}88`; }}
                    onMouseLeave={(e) => { if (!isDone) e.currentTarget.style.borderColor = C.border; }}
                  >
                    {isDone ? (<><Check size={13} strokeWidth={3}/> Completed</>) : (<>Mark complete</>)}
                  </button>
                </div>
              );
            })()}

            {/* Nav. The same two buttons render twice: inline in the page
                for desktop, and in a fixed bar for phones, where CSS hides
                whichever one does not apply. Rendering both beats hoisting
                the markup into a shared component that then has to be
                threaded a dozen props it only uses to look identical.

                Previous disappears entirely on page one rather than
                rendering disabled: a dead button taking 40% of the row on
                a 390px screen is worse than no button. */}
            {!(chapter.bare && total === 1) && (
            <div className="mc-inline-nav" style={{display:"flex",gap:10,marginTop:18}}>
              {active > 0 && (
              <button onClick={() => goPage(a => Math.max(0, a-1))}
                style={{flex:1,padding:"12px 0",borderRadius:10,fontSize:13,fontWeight:700,cursor:"pointer",
                  border:`1px solid ${C.border}`,background:"transparent",color:C.textMid,
                  display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                <ChevronLeft size={15}/> Previous
              </button>
              )}
              {active < total - 1 ? (
                <button onClick={() => { markChapterComplete(chapter.id); goPage(a => Math.min(total-1, a+1)); }}
                  style={{flex:2,minWidth:0,padding:"12px 14px",borderRadius:10,fontSize:13,fontWeight:800,cursor:"pointer",
                    border:"none",background:GOLD,color:"#111",
                    display:"flex",alignItems:"center",justifyContent:"center",gap:6,boxShadow:`0 4px 14px ${GOLD}44`}}>
                  <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",minWidth:0,flex:"0 1 auto"}}>
                    {chapterDisplayTitle(module.chapters[active + 1]?.title) || "Next chapter"}
                  </span>
                  <ChevronRight size={15} style={{flexShrink:0}}/>
                </button>
              ) : (
                <button onClick={() => { markChapterComplete(chapter.id); onClose && onClose(); }}
                  style={{flex:2,padding:"12px 0",borderRadius:10,fontSize:13,fontWeight:800,cursor:"pointer",
                    border:"none",background:GOLD,color:"#111",
                    display:"flex",alignItems:"center",justifyContent:"center",gap:6,boxShadow:`0 4px 14px ${GOLD}44`}}>
                  Finish
                </button>
              )}
            </div>
            )}

            {/* Discussion thread — signed-in members only (no comments in the
                public / email-gated preview), and only on the last page.
                It is one thread for the whole masterclass, so repeating it
                under all 21 pages was 21 invitations to the same
                conversation sitting under content that is still mid
                argument. At the end it is the natural next thing to do. */}
            {!publicMode && active === total - 1 && (
              <MasterclassComments moduleKey={moduleKey} C={C} dark={dark}
                title={module.discussion?.title} prompt={module.discussion?.prompt}/>
            )}

            {/* Public preview: persistent CTA block at the end of each
                chapter. Suppressed on chapter 1 — the goal there is to
                push the coach to click "Next chapter" and hit the email
                gate, not to short-circuit them into the platform signup
                before they've even given us their email. From chapter 2
                onward, where they've already submitted their lead, the
                CTA reads as a softer upsell. */}
            {publicMode && active > 0 && !module.hidePublicCta && (
              <div style={{marginTop:28,padding:"22px 22px",borderRadius:14,border:`1px solid ${GOLD}55`,background:`linear-gradient(135deg, ${GOLD}15 0%, ${GOLD}03 100%)`}}>
                <div style={{fontSize:11,fontWeight:800,color:GOLD,letterSpacing:1.2,textTransform:"uppercase",marginBottom:6}}>You're previewing</div>
                <div style={{fontSize:17,fontWeight:800,color:C.text,marginBottom:6,lineHeight:1.3}}>Unlock the full coaches platform</div>
                <div style={{fontSize:13,color:C.textMid,lineHeight:1.6,marginBottom:16}}>
                  Full masterclass library, team + player-dev drills, X&amp;O breakdowns, practice plans, Playbook Builder, and the full Rikki Broadmore drill library.
                </div>
                <PublicCtaPair C={C}/>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* The sticky conversion bar, in publicMode on every size, ONCE THE
          READER HAS STARTED.
          It used to be mobile only, standing in for a topbar CTA pair that
          did not fit under 768px. The topbar is gone on a public article
          now, so this is the only ask, and it belongs on desktop for the
          same reason it belonged on a phone: in reach, and out of the way
          of the first paragraph.

          It used to be there from the first pixel, which on a phone meant a
          full width gold slab sitting under the opening paragraph with
          Safari's own bar half over it. Coleman's word was "annoying", and
          he was right: asking somebody to sign up before they have read a
          sentence is the ask arriving before the thing it is asking about.

          So it waits for 12% of the page, and when it comes it is a pill
          rather than a slab: the gold is the button, not the bar. */}
      {publicMode && scrollPct > 0.12 && !module.hidePublicCta && (
        <div style={{
          position:"absolute",left:0,right:0,bottom:0,
          padding:"10px 12px calc(10px + env(safe-area-inset-bottom))",
          background:dark?"rgba(10,10,10,0.96)":"rgba(255,255,255,0.96)",
          backdropFilter:"blur(8px)",
          WebkitBackdropFilter:"blur(8px)",
          borderTop:`1px solid ${C.border}`,
          display:"flex",gap:8,zIndex:50,
          justifyContent:"center",
          animation:"mcAskIn .28s ease-out both",
        }}>
          <style>{`@keyframes mcAskIn{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}`}</style>
          <button
            onClick={() => { window.location.hash = "#/landing"; }}
            style={{
              flex:isMobile?1:"0 0 160px",padding:"12px 0",borderRadius:10,
              background:"transparent",color:C.text,fontSize:13,fontWeight:700,
              border:`1px solid ${C.border}`,cursor:"pointer",
              minHeight:44,
            }}>
            Read more
          </button>
          <button
            onClick={() => { window.location.hash = "#/signup"; }}
            style={{
              flex:isMobile?2:"0 0 260px",padding:"12px 0",borderRadius:10,
              background:GOLD,color:"#111",fontSize:13.5,fontWeight:800,
              border:"none",cursor:"pointer",
              boxShadow:`0 4px 14px ${GOLD}44`,minHeight:44,
            }}>
            Sign up for full access
          </button>
        </div>
      )}
    </div>
  );
}

export default MasterclassReader;
